import { useState, useRef, useEffect } from "react";
import './mascot.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const TC = { overlap: "#2AEEFD", user1: "#43B1C1", user2: "#43B1C1", joint: "#2AEEFD", nick: "#43B1C1", geri: "#43B1C1" };
const COLORS = { added: "#2AEEFD", info: "#43B1C1", error: "#ff6b6b", flag: "#2AEEFD", question: "#2AEEFD", user: "#4a7a9b" };
const ICONS  = { added: "✦", info: "·", error: "✕", flag: "⚑", question: "?", user: "›" };

// Derived from settings at render time — see buildTI() and buildCoverageLabels()
function buildTI(settings) {
  const ui = settings?.user_icons || {};
  const u1 = ui.user1 || ui.nick || "🔵";
  const u2 = ui.user2 || ui.geri || "🔴";
  return {
    user1: u1, user2: u2,
    joint: ui.both || "🟣",
    overlap: ui.conflict || "⚠️",
    // backward compat aliases for old travel event types
    nick: u1, geri: u2, overlap_type: ui.conflict || "⚠️",
  };
}
function buildCoverageLabels(settings) {
  const opts = settings?.coverage_options;
  if (!opts) return { user1_off: "User 1 off", user2_off: "User 2 off", both_off: "Both off", backup_school: "Backup school", camp: "Camp", other: "Other", no_plan: "No plan" };
  const out = {};
  opts.forEach(o => { out[o.value] = (o.emoji ? o.emoji + " " : "") + o.label; });
  return out;
}

// Emoji picker palette — grouped for the settings UI
const EMOJI_PALETTE = [
  "🔵","🔴","🟣","🟢","🟡","🟠","⚪","🔶","🔷","⭐","💛","💙","💜","❤️","🧡","💚","🖤","🤍",
  "👤","👩","👨","🧑","🙋","🙋‍♂️","🧔","👦","👧","🧒",
  "⚠️","🚨","❌","⛔","💢","❗","🚫",
  "🏫","⛺","📌","❓","✅","🏠","🛡️","🎒","📅","🎯","🏃","💼","🚗","✈️","🌴","🎪","👍","🎉","🌟",
];

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function todayStr() {
  return "Today is " + new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) + ". ";
}

// Reload page on expired session so login screen appears instead of empty data
function reloadIf401(res) { if (res.status === 401) { window.location.reload(); return true; } return false; }

function mondayOf(d) {
  const date = new Date(d);
  const day = date.getDay();
  date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  return date.toISOString().slice(0, 10);
}

function fmtRefreshed(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " at " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const C = {
  navy: "#00294C",
  navyDark: "#001d38",
  cyan: "#43B1C1",
  cyanBright: "#2AEEFD",
  white: "#ffffff",
  offWhite: "#e8eef4",
  muted: "#7a9ab5",
  border: "#0d3a56",
  red: "#ff6b6b",
  green: "#4caf50",
};

const inputBase = {
  background: C.navyDark,
  border: `1px solid ${C.border}`,
  borderRadius: 0,
  padding: "14px 16px",
  color: C.white,
  fontSize: 16,
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
  letterSpacing: "0.02em",
  WebkitAppearance: "none",
};

const mkBtn = (variant, disabled) => {
  const variants = {
    primary: { background: "transparent", border: `1.5px solid ${C.cyanBright}`, color: C.cyanBright },
    cyan:    { background: C.cyan, border: `1.5px solid ${C.cyan}`, color: C.navyDark },
    ghost:   { background: "transparent", border: "none", color: C.muted },
    danger:  { background: "transparent", border: `1.5px solid ${C.red}`, color: C.red },
  };
  const v = variants[variant] || variants.primary;
  return {
    ...v, borderRadius: 0, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
    textTransform: "uppercase", cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.35 : 1, transition: "all 0.2s", fontFamily: "inherit",
    WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
  };
};

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: C.cyan, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 12 }}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ width: "100%", borderTop: `1px solid ${C.border}`, margin: "28px 0" }} />;
}

// ── Weekly Digest ─────────────────────────────────────────────────────────────

function DayGlance({ settings }) {
  const [tab, setTab] = useState('today');
  const [eventsMap, setEventsMap] = useState({});
  const [noschoolBlocks, setNoschoolBlocks] = useState([]);
  const [travel, setTravel] = useState([]);
  const [todos, setTodos] = useState([]);
  const [mealsMap, setMealsMap] = useState({});
  const [loading, setLoading] = useState(true);

  const todayISO  = new Date().toISOString().slice(0, 10);
  const tmrDate   = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
  const tmrISO    = tmrDate.toISOString().slice(0, 10);

  const dayNameOf = (iso) => ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date(iso + 'T12:00:00').getDay()];

  useEffect(() => {
    const todayWeek = mondayOf(new Date(todayISO + 'T12:00:00'));
    const tmrWeek   = mondayOf(new Date(tmrISO + 'T12:00:00'));
    const weekStarts = [...new Set([todayWeek, tmrWeek])];

    Promise.all([
      fetch(`${BACKEND}/api/day-events?date=${todayISO}`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ events: [] })),
      fetch(`${BACKEND}/api/day-events?date=${tmrISO}`,   { credentials: 'include' }).then(r => r.json()).catch(() => ({ events: [] })),
      fetch(`${BACKEND}/api/noschool/blocks`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ blocks: [] })),
      fetch(`${BACKEND}/api/digest`,          { credentials: 'include' }).then(r => r.json()).catch(() => ({})),
      fetch(`${BACKEND}/api/todos`,           { credentials: 'include' }).then(r => r.json()).catch(() => ({ open: [] })),
      ...weekStarts.map(ws => fetch(`${BACKEND}/api/meals?week_start=${ws}`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ meals: [] }))),
    ]).then(([todayEvts, tmrEvts, noSchoolData, digestData, todosData, ...mealsResults]) => {
      setEventsMap({ [todayISO]: todayEvts.events || [], [tmrISO]: tmrEvts.events || [] });
      setNoschoolBlocks(noSchoolData.blocks || []);
      setTravel(digestData.conflicts?.travel || []);
      setTodos(todosData.open || []);

      const mMap = {};
      mealsResults.forEach((res, i) => {
        for (const m of (res.meals || [])) {
          if (m.meal_text) mMap[`${weekStarts[i]}|${m.day}|${m.meal_type}|${m.child_name}`] = m;
        }
      });
      setMealsMap(mMap);
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dateKey    = tab === 'today' ? todayISO : tmrISO;
  const dayEvents  = eventsMap[dateKey] || [];
  const dayNS      = noschoolBlocks.filter(b => b.date_start <= dateKey && b.date_end >= dateKey);
  const dayTravel  = travel.filter(t => t.date_start && t.date_end && t.date_start <= dateKey && t.date_end >= dateKey);
  const dayTodos   = todos.filter(t => t.target_date && (tab === 'today' ? t.target_date <= dateKey : t.target_date === dateKey));

  const weekStart  = mondayOf(new Date(dateKey + 'T12:00:00'));
  const dayName    = dayNameOf(dateKey);
  const dayMeals   = Object.entries(mealsMap)
    .filter(([k]) => k.startsWith(`${weekStart}|${dayName}|`))
    .map(([, m]) => m);

  const TI     = buildTI(settings);
  const CL     = buildCoverageLabels(settings);
  const rowSt  = { display: 'flex', alignItems: 'flex-start', gap: 12, borderTop: `1px solid ${C.border}`, paddingTop: 8, paddingBottom: 8 };
  const lblSt  = { fontSize: 9, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', minWidth: 72, flexShrink: 0, paddingTop: 2 };

  function fmtT(dt) {
    if (!dt) return '';
    return new Date(dt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  const hasData = dayEvents.length > 0 || dayNS.length > 0 || dayTravel.length > 0 || dayTodos.length > 0 || dayMeals.length > 0;

  return (
    <div style={{ background: C.navy, border: `1px solid ${C.border}`, padding: '14px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.cyan, textTransform: 'uppercase', letterSpacing: '0.15em' }}>Day at a Glance</span>
        <div style={{ display: 'flex' }}>
          {['today', 'tomorrow'].map((t, i) => (
            <button key={t} onClick={() => setTab(t)} style={{
              background: 'none', border: `1px solid ${C.border}`,
              borderRight: i === 0 ? 'none' : `1px solid ${C.border}`,
              color: tab === t ? C.cyan : C.muted, fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.1em', padding: '4px 10px',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ fontSize: 11, color: C.muted, padding: '4px 0' }}>Loading…</div>
      ) : !hasData ? (
        <div style={{ fontSize: 12, color: C.muted, paddingTop: 4 }}>All clear {tab === 'today' ? 'today' : 'tomorrow'}</div>
      ) : (
        <>
          {dayEvents.length > 0 && (
            <div style={rowSt}>
              <span style={lblSt}>Events</span>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                {dayEvents.map((e, i) => (
                  <div key={e.id || i} style={{ color: C.offWhite }}>
                    {!e.allDay && e.start?.dateTime && <span style={{ color: C.muted, marginRight: 5 }}>{fmtT(e.start.dateTime)}</span>}
                    {e.summary}
                    {e.source && e.source !== 'shared' && <span style={{ color: C.muted, fontSize: 10, marginLeft: 4 }}>· {e.source}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {dayNS.length > 0 && (
            <div style={rowSt}>
              <span style={lblSt}>No School</span>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                {dayNS.map((b, i) =>
                  (b.children || []).map((c, ci) => {
                    const cvLabel = CL[c.coverage_type] || c.coverage_type || '';
                    const noplan  = !c.coverage_type || c.coverage_type === 'no_plan';
                    return (
                      <div key={`${i}_${ci}`} style={{ color: noplan ? C.red : C.offWhite }}>
                        {c.child_icon} {c.child_name}: {b.label}{cvLabel && !noplan ? ` · ${cvLabel}` : ''}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {dayTravel.length > 0 && (
            <div style={rowSt}>
              <span style={lblSt}>Travel</span>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                {dayTravel.map((t, i) => (
                  <div key={i} style={{ color: C.offWhite }}>{TI[t.type] || ''} {t.summary}</div>
                ))}
              </div>
            </div>
          )}

          {dayMeals.length > 0 && (
            <div style={rowSt}>
              <span style={lblSt}>Meals</span>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                {dayMeals.map((m, i) => (
                  <div key={i} style={{ color: C.offWhite }}>
                    {m.meal_text}
                    {m.child_name ? <span style={{ color: C.muted, fontSize: 10, marginLeft: 4 }}>· {m.child_name}'s {m.meal_type}</span>
                      : <span style={{ color: C.muted, fontSize: 10, marginLeft: 4 }}>· {m.meal_type}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {dayTodos.length > 0 && (
            <div style={{ ...rowSt, paddingBottom: 0 }}>
              <span style={lblSt}>Tasks</span>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                {dayTodos.map((t, i) => (
                  <div key={i} style={{ color: t.target_date < dateKey ? C.red : C.offWhite }}>
                    {t.title}{t.target_date < dateKey ? ' · overdue' : ''}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WeekDigest({ settings }) {
  const [weekTravel, setWeekTravel]         = useState([]);
  const [weekNoSchool, setWeekNoSchool]     = useState([]);
  const [openTodos, setOpenTodos]           = useState([]);
  const [weekendActivities, setWeekendActivities] = useState(null); // null = no cache yet

  const today = new Date();
  const sun = new Date(today); sun.setDate(today.getDate() - today.getDay()); sun.setHours(0, 0, 0, 0);
  const sat = new Date(sun);   sat.setDate(sun.getDate() + 6);               sat.setHours(23, 59, 59, 999);
  const weekLabel = sun.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
                    " – " + sat.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  useEffect(() => {
    const inWeek = (s, e) => new Date(s + 'T12:00:00') <= sat && new Date(e + 'T12:00:00') >= sun;
    fetch(`${BACKEND}/api/digest`, { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        if (d.conflicts?.travel) setWeekTravel(d.conflicts.travel.filter(t => t.date_start && t.date_end && inWeek(t.date_start, t.date_end)));
        const wc = d.weekend;
        if (wc?.events) {
          const DAY_LABEL = { fri: 'Fri', sat: 'Sat', sun: 'Sun' };
          const acts = wc.events.filter(e => e.isActivity && !e.allDay);
          const byDay = {};
          for (const ev of acts) { if (!ev.day) continue; if (!byDay[ev.day]) byDay[ev.day] = []; byDay[ev.day].push(ev.summary); }
          const parts = ['fri', 'sat', 'sun'].filter(d => byDay[d]?.length).map(d => `${DAY_LABEL[d]}: ${byDay[d].join(', ')}`);
          setWeekendActivities(parts.length ? parts : []);
        } else { setWeekendActivities([]); }
      })
      .catch(() => { setWeekendActivities([]); });
    fetch(`${BACKEND}/api/noschool/blocks`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (d.blocks) setWeekNoSchool(d.blocks.filter(b => inWeek(b.date_start, b.date_end))); })
      .catch(() => {});
    fetch(`${BACKEND}/api/todos`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setOpenTodos(d.open || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const overdueCount = openTodos.filter(t => t.target_date && t.target_date < today.toISOString().slice(0, 10)).length;
  const rowStyle = { display: "flex", alignItems: "baseline", gap: 12, borderTop: `1px solid ${C.border}`, paddingTop: 8, paddingBottom: 8 };
  const labelStyle = { fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.12em", minWidth: 72, flexShrink: 0 };

  return (
    <div style={{ background: C.navy, border: `1px solid ${C.border}`, padding: "14px 18px", marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.cyan, textTransform: "uppercase", letterSpacing: "0.15em" }}>Week at a Glance</span>
        <span style={{ fontSize: 10, color: C.muted }}>{weekLabel}</span>
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>Travel</span>
        <span style={{ fontSize: 12, color: weekTravel.length ? C.offWhite : C.muted, lineHeight: 1.4 }}>
          {weekTravel.length ? weekTravel.map(t => `${buildTI(settings)[t.type] || t.type} ${t.summary}`).join("  ·  ") : "None"}
        </span>
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>No School</span>
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          {weekNoSchool.length === 0
            ? <span style={{ color: C.muted }}>None</span>
            : weekNoSchool.map((b, i) => {
                const d = new Date(b.date_start + 'T12:00:00');
                const dateLabel = `${d.getMonth()+1}/${d.getDate()}`;
                const reason = buildCoverageLabels(settings)[b.coverage_type] || b.coverage_type;
                const note = b.note ? ` – ${b.note}` : '';
                return (
                  <div key={i} style={{ color: b.coverage_type === 'no_plan' ? C.red : C.offWhite }}>
                    <span style={{ color: C.muted }}>{dateLabel}:</span> {reason}{note}
                  </div>
                );
              })
          }
        </div>
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>Weekend</span>
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          {weekendActivities === null || weekendActivities.length === 0
            ? <span style={{ color: C.muted }}>{weekendActivities === null ? '—' : 'Nothing planned · open Weekend tab to load'}</span>
            : weekendActivities.map((line, i) => <div key={i} style={{ color: C.offWhite }}>{line}</div>)
          }
        </div>
      </div>

      <div style={{ ...rowStyle, paddingBottom: 0 }}>
        <span style={labelStyle}>To-Do</span>
        <span style={{ fontSize: 12, color: openTodos.length === 0 ? C.green : overdueCount > 0 ? C.red : C.offWhite, lineHeight: 1.4 }}>
          {openTodos.length === 0 ? "All clear" : `${openTodos.length} open${overdueCount > 0 ? ` · ${overdueCount} overdue` : ""}`}
        </span>
      </div>
    </div>
  );
}

function fmtTravelDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const opts = { weekday: 'short', month: 'short', day: 'numeric' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

function TravelRow({ c, settings }) {
  const TI = buildTI(settings);
  const dateLabel = c.date_start
    ? (!c.date_end || c.date_start === c.date_end
        ? fmtTravelDate(c.date_start)
        : fmtTravelDate(c.date_start) + ' – ' + fmtTravelDate(c.date_end))
    : c.dates;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", borderLeft: `2px solid ${TC[c.type] || TC.nick}`, marginBottom: 4, background: C.navyDark }}>
      <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{TI[c.type] || c.type}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: C.cyan, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", marginBottom: 2 }}>{dateLabel}</div>
        <div style={{ color: C.offWhite, fontSize: 13, lineHeight: 1.5, wordBreak: "break-word" }}>{c.summary}</div>
      </div>
    </div>
  );
}

// ── No School Manager ─────────────────────────────────────────────────────────

function NoSchoolManager({ settings }) {
  const [status, setStatus]       = useState("loading");
  const [blocks, setBlocks]       = useState([]);
  const [summary, setSummary]     = useState({ total: 0, unplanned: 0 });
  const [calError, setCalError]   = useState("");
  const [savingIdx, setSavingIdx] = useState(null);
  const [togglingCal, setTogglingCal] = useState(null);

  useEffect(() => { loadBlocks(); }, []);

  async function loadBlocks() {
    setStatus("loading");
    setCalError("");
    try {
      const res  = await fetch(`${BACKEND}/api/noschool/blocks`, { credentials: "include" });
      if (reloadIf401(res)) return;
      const data = await res.json();
      if (data.calendarError) { setCalError(data.error); setStatus("error"); }
      else { setBlocks(data.blocks || []); setSummary(data.summary || { total: 0, unplanned: 0 }); setStatus("loaded"); }
    } catch (e) {
      setCalError(e.message); setStatus("error");
    }
  }

  async function saveCoverage(idx, childIdx, overrides = {}) {
    const b = blocks[idx];
    const child = b.children ? { ...(b.children[childIdx] || {}), ...overrides } : { ...b, ...overrides };
    setSavingIdx(idx);
    try {
      await fetch(`${BACKEND}/api/noschool/coverage`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          date_start: b.date_start, date_end: b.date_end,
          label: b.label,
          coverage_type: child.coverage_type,
          note: child.note,
          child_name: child.child_name,
        }),
      });
    } catch (_) { /* silent */ }
    setSavingIdx(null);
  }

  function updateBlock(idx, patch, childIdx = null) {
    setBlocks(prev => {
      const next = prev.map((b, i) => {
        if (i !== idx) return b;
        if (childIdx !== null && b.children) {
          const newChildren = b.children.map((c, ci) => ci === childIdx ? { ...c, ...patch } : c);
          return { ...b, children: newChildren };
        }
        return { ...b, ...patch };
      });
      // Count unplanned: any block where all children (or block itself) are no_plan
      const unplanned = next.filter(b => {
        if (b.children?.length) return b.children.every(c => c.coverage_type === "no_plan");
        return b.coverage_type === "no_plan";
      }).length;
      setSummary(s => ({ ...s, unplanned }));
      return next;
    });
  }

  async function onCoverageChange(idx, val, childIdx = null) {
    updateBlock(idx, { coverage_type: val }, childIdx);
    await saveCoverage(idx, childIdx, { coverage_type: val });
  }

  async function onNoteBlur(idx, childIdx = null) {
    await saveCoverage(idx, childIdx);
  }

  async function toggleCalendar(idx, childIdx = null) {
    const toggleKey = `${idx}_${childIdx}`;
    if (togglingCal === toggleKey) return;
    setTogglingCal(toggleKey);
    const b = blocks[idx];
    const child = (b.children && childIdx !== null) ? b.children[childIdx] : b;
    setCalError("");
    try {
      const res  = await fetch(`${BACKEND}/api/noschool/calendar-toggle`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          date_start: b.date_start, date_end: b.date_end, label: b.label,
          coverage_type: child.coverage_type, note: child.note,
          added_to_calendar: child.added_to_calendar, calendar_event_id: child.calendar_event_id,
          child_name: child.child_name,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        updateBlock(idx, { added_to_calendar: data.added_to_calendar, calendar_event_id: data.calendar_event_id }, childIdx);
      } else {
        setCalError(data.error || "Could not update calendar.");
      }
    } catch (e) { setCalError("Could not reach server."); }
    setTogglingCal(null);
  }

  function blockBorderColor(b) {
    const types = (b.children || []).map(c => c.coverage_type);
    if (!types.length) return C.green;
    if (types.some(t => t === 'no_plan' || !t)) return C.red;
    if (types.some(t => t === 'camp')) return C.cyan;
    return C.green;
  }

  // ── render states ────────────────────────────────────────────────────────

  if (status === "loading") return (
    <div style={{ padding: "48px 16px", textAlign: "center", color: C.muted, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase" }}>
      Loading school calendar…
    </div>
  );

  if (status === "error") return (
    <div style={{ maxWidth: 620, margin: "0 auto", padding: "32px 16px" }}>
      <div style={{ color: C.red, fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>{calError || "Could not load school calendar."}</div>
      <button onClick={loadBlocks} style={{ ...mkBtn("primary", false), padding: "14px 24px" }}>Retry</button>
    </div>
  );

  // ── main view ────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 620, margin: "0 auto", padding: "32px 16px 80px" }}>

      {/* Summary banner */}
      <div style={{ background: C.navy, border: `1px solid ${C.border}`, padding: "16px 20px", marginBottom: 28, display: "flex", alignItems: "center", gap: 0 }}>
        <div style={{ marginRight: 24 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.white, lineHeight: 1 }}>{summary.total}</div>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 4 }}>No-School Blocks</div>
        </div>
        <div style={{ width: 1, height: 36, background: C.border, marginRight: 24 }} />
        <div style={{ marginRight: "auto" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: summary.unplanned > 0 ? C.red : C.green, lineHeight: 1 }}>{summary.unplanned}</div>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 4 }}>Need Coverage</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: C.muted }}>
          <span style={{ borderLeft: `3px solid ${C.red}`,   paddingLeft: 8 }}>No plan</span>
          <span style={{ borderLeft: `3px solid ${C.cyan}`,  paddingLeft: 8 }}>Camp</span>
          <span style={{ borderLeft: `3px solid ${C.green}`, paddingLeft: 8 }}>Covered</span>
        </div>
      </div>

      {calError && (
        <div style={{ color: C.red, fontSize: 12, marginBottom: 16, padding: "10px 14px", background: "rgba(255,107,107,0.08)", border: `1px solid ${C.red}` }}>
          {calError}
        </div>
      )}

      {blocks.length === 0 && (
        <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "40px 16px" }}>
          No no-school days found in the current school year.
        </div>
      )}

      {blocks.map((block, idx) => {
        const covOpts = settings?.coverage_options || [
          { value: 'user1_off', label: 'User 1 off', emoji: '🔵' },
          { value: 'user2_off', label: 'User 2 off', emoji: '🔴' },
          { value: 'both_off', label: 'Both off', emoji: '🟣' },
          { value: 'backup_school', label: 'Backup school', emoji: '🏫' },
          { value: 'camp', label: 'Camp', emoji: '⛺' },
          { value: 'other', label: 'Other', emoji: '📌' },
          { value: 'no_plan', label: 'No plan', emoji: '❓' },
        ];
        // Per-child view
        const children = block.children;
        return (
          <div key={`${block.date_start}_${block.date_end}`}
            style={{ borderLeft: `3px solid ${blockBorderColor(block)}`, background: C.navy, marginBottom: 8, padding: "14px 16px" }}>
            {/* Header */}
            <div style={{ color: C.cyan, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", marginBottom: 6 }}>
              {block.display}
              {savingIdx === idx && <span style={{ color: C.muted, fontWeight: 400, marginLeft: 8 }}>saving…</span>}
            </div>
            <div style={{ color: C.offWhite, fontSize: 13, lineHeight: 1.4, wordBreak: "break-word", marginBottom: 10 }}>{block.label}</div>

            {children && children.length > 0 ? (
              // Per-child rows
              children.map((child, ci) => (
                <div key={child.child_id || ci} style={{ borderTop: ci > 0 ? `1px solid ${C.border}` : 'none', paddingTop: ci > 0 ? 10 : 0, marginTop: ci > 0 ? 10 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {child.child_icon && <span style={{ fontSize: 14 }}>{child.child_icon}</span>}
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>{child.child_name}</span>
                      {child.auto_camp && <span style={{ fontSize: 9, color: C.cyan, letterSpacing: "0.06em" }}>AUTO·CAMP</span>}
                    </div>
                    <button onClick={() => toggleCalendar(idx, ci)}
                      disabled={togglingCal === `${idx}_${ci}`}
                      style={{ ...mkBtn(child.added_to_calendar ? "cyan" : "primary", false), padding: "6px 10px", fontSize: 9, flexShrink: 0, whiteSpace: "nowrap", opacity: togglingCal === `${idx}_${ci}` ? 0.5 : 1 }}>
                      {togglingCal === `${idx}_${ci}` ? "..." : child.added_to_calendar ? "✓ On Cal" : "+ Cal"}
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <select value={child.coverage_type || 'no_plan'}
                      onChange={e => onCoverageChange(idx, e.target.value, ci)}
                      style={{ ...inputBase, flex: "1 1 140px", padding: "8px 10px", fontSize: 12, cursor: "pointer" }}>
                      {covOpts.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.emoji ? opt.emoji + " " : ""}{opt.label}</option>
                      ))}
                    </select>
                    <input type="text" placeholder="Notes…"
                      value={child.note || ""}
                      onChange={e => updateBlock(idx, { note: e.target.value }, ci)}
                      onBlur={() => onNoteBlur(idx, ci)}
                      style={{ ...inputBase, flex: "2 1 160px", padding: "8px 10px", fontSize: 12 }}
                    />
                  </div>
                </div>
              ))
            ) : (
              // Legacy single-child view
              <div>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                  {block.auto_camp && <div style={{ fontSize: 10, color: C.cyan, letterSpacing: "0.06em", opacity: 0.8 }}>AUTO · CAMP DETECTED</div>}
                  <button onClick={() => toggleCalendar(idx, null)}
                    disabled={togglingCal === `${idx}_null`}
                    style={{ ...mkBtn(block.added_to_calendar ? "cyan" : "primary", false), padding: "8px 12px", fontSize: 10, flexShrink: 0, whiteSpace: "nowrap", marginLeft: "auto", opacity: togglingCal === `${idx}_null` ? 0.5 : 1 }}>
                    {togglingCal === `${idx}_null` ? "..." : block.added_to_calendar ? "✓ On Cal" : "+ Cal"}
                  </button>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <select value={block.coverage_type || 'no_plan'}
                    onChange={e => onCoverageChange(idx, e.target.value)}
                    style={{ ...inputBase, flex: "1 1 140px", padding: "10px 12px", fontSize: 13, cursor: "pointer" }}>
                    {covOpts.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.emoji ? opt.emoji + " " : ""}{opt.label}</option>
                    ))}
                  </select>
                  <input type="text" placeholder="Notes…"
                    value={block.note || ""}
                    onChange={e => updateBlock(idx, { note: e.target.value })}
                    onBlur={() => onNoteBlur(idx)}
                    style={{ ...inputBase, flex: "2 1 160px", padding: "10px 12px", fontSize: 13 }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Todo Title inline editor ──────────────────────────────────────────────────

function TodoTitle({ t, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(t.title);
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  function commit() {
    const trimmed = val.trim();
    if (trimmed && trimmed !== t.title) onSave(trimmed);
    else setVal(t.title);
    setEditing(false);
  }

  if (editing) {
    return (
      <input ref={inputRef} value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(t.title); setEditing(false); } }}
        style={{ background: 'transparent', border: 'none', borderBottom: `1px solid ${C.cyanBright}`, color: C.offWhite, fontSize: 14, lineHeight: 1.4, width: '100%', outline: 'none', fontFamily: 'inherit', padding: '0 0 2px 0', marginBottom: 2 }} />
    );
  }
  return (
    <div onClick={() => setEditing(true)}
      style={{ color: C.offWhite, fontSize: 14, lineHeight: 1.4, wordBreak: 'break-word', cursor: 'text' }}
      title="Click to edit">
      {t.title}
    </div>
  );
}

// ── Todo Manager ─────────────────────────────────────────────────────────────

function TodoManager({ settings }) {
  const [open, setOpen]           = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [title, setTitle]         = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [responsible, setResponsible] = useState("both");
  const [adding, setAdding]       = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => { loadTodos(); }, []);

  async function loadTodos() {
    setLoading(true);
    try {
      const res  = await fetch(`${BACKEND}/api/todos`, { credentials: "include" });
      if (reloadIf401(res)) return;
      const data = await res.json();
      setOpen(data.open || []);
      setCompleted(data.completed || []);
    } catch (_) {}
    setLoading(false);
  }

  async function addTodo() {
    if (!title.trim()) return;
    setAdding(true);
    try {
      const res  = await fetch(`${BACKEND}/api/todos`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title: title.trim(), target_date: targetDate || null, responsible }),
      });
      const data = await res.json();
      if (data.ok) {
        setOpen(prev => [...prev, data.todo]);
        setTitle(""); setTargetDate(""); setResponsible("both");
        titleRef.current?.focus();
      }
    } catch (_) {}
    setAdding(false);
  }

  async function patchTodo(id, patch) {
    const r = await fetch(`${BACKEND}/api/todos/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify(patch),
    });
    const data = await r.json();
    if (data.ok) {
      setOpen(prev => prev.map(t => t.id === id ? data.todo : t));
      setCompleted(prev => prev.map(t => t.id === id ? data.todo : t));
    }
  }

  async function toggleComplete(id, currentlyCompleted) {
    const r = await fetch(`${BACKEND}/api/todos/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ completed: !currentlyCompleted }),
    });
    const data = await r.json();
    if (!data.ok) return;
    if (!currentlyCompleted) {
      setOpen(prev => prev.filter(t => t.id !== id));
      setCompleted(prev => [data.todo, ...prev]);
    } else {
      setCompleted(prev => prev.filter(t => t.id !== id));
      setOpen(prev => [...prev, data.todo]);
    }
  }

  async function deleteTodo(id, isCompleted) {
    await fetch(`${BACKEND}/api/todos/${id}`, { method: "DELETE", credentials: "include" });
    if (isCompleted) setCompleted(prev => prev.filter(t => t.id !== id));
    else setOpen(prev => prev.filter(t => t.id !== id));
  }

  function isOverdue(t) {
    return t.target_date && t.target_date < new Date().toISOString().slice(0, 10);
  }

  function fmtDate(d) {
    if (!d) return null;
    return new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  // Sort open: overdue first, then by target_date (nulls last), then by created_at
  const sortedOpen = [...open].sort((a, b) => {
    const aOver = isOverdue(a) ? 0 : 1;
    const bOver = isOverdue(b) ? 0 : 1;
    if (aOver !== bOver) return aOver - bOver;
    if (a.target_date && b.target_date) return a.target_date.localeCompare(b.target_date);
    if (a.target_date) return -1;
    if (b.target_date) return 1;
    return new Date(a.created_at) - new Date(b.created_at);
  });

  if (loading) return (
    <div style={{ padding: "48px 16px", textAlign: "center", color: C.muted, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase" }}>
      Loading…
    </div>
  );

  return (
    <div style={{ maxWidth: 620, margin: "0 auto", padding: "32px 16px 80px" }}>

      {/* Add form */}
      <div style={{ marginBottom: 32 }}>
        <SectionLabel>Add To-Do</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input ref={titleRef} type="text" placeholder="What needs to get done?"
            value={title} onChange={e => setTitle(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !adding && addTodo()}
            style={{ ...inputBase, width: "100%" }} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)}
              style={{ ...inputBase, flex: "1 1 140px", colorScheme: "dark", cursor: "pointer" }} />
            <select value={responsible} onChange={e => setResponsible(e.target.value)}
              style={{ ...inputBase, flex: "0 0 auto", padding: "10px 12px", fontSize: 13, cursor: "pointer" }}>
              <option value="both">{(settings?.user_icons?.both || "🟣")} Both</option>
              <option value="nick">{(settings?.user_icons?.user1 || settings?.user_icons?.nick || "🔵")} {settings?.user1_name || "User 1"}</option>
              <option value="geri">{(settings?.user_icons?.user2 || settings?.user_icons?.geri || "🔴")} {settings?.user2_name || "User 2"}</option>
            </select>
            <button onClick={addTodo} disabled={!title.trim() || adding}
              style={{ ...mkBtn("primary", !title.trim() || adding), padding: "14px 24px", fontSize: 13, flexShrink: 0 }}>
              {adding ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      </div>

      <Divider />

      {/* Open todos */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
          <SectionLabel>Open · {sortedOpen.length}</SectionLabel>
        </div>

        {sortedOpen.length === 0 && (
          <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "32px 16px" }}>
            Nothing open. Nice.
          </div>
        )}

        {sortedOpen.map(t => (
          <div key={t.id} style={{ background: C.navy, borderLeft: `3px solid ${isOverdue(t) ? C.red : C.border}`, marginBottom: 6, padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 12 }}>
            <button onClick={() => toggleComplete(t.id, false)}
              style={{ width: 20, height: 20, borderRadius: 2, border: `1.5px solid ${C.muted}`, background: "transparent", cursor: "pointer", flexShrink: 0, marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <TodoTitle t={t} onSave={title => patchTodo(t.id, { title })} />
              {t.target_date && (
                <div style={{ marginTop: 4, display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: isOverdue(t) ? C.red : C.muted, borderLeft: `2px solid ${isOverdue(t) ? C.red : C.border}`, paddingLeft: 6 }}>
                  {isOverdue(t) ? "Overdue · " : ""}{fmtDate(t.target_date)}
                </div>
              )}
            </div>
            <select value={t.responsible || "both"}
              onChange={e => patchTodo(t.id, { responsible: e.target.value })}
              style={{ background: C.navyDark, border: `1px solid ${C.border}`, color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 6px", cursor: "pointer", fontFamily: "inherit", flexShrink: 0, alignSelf: "flex-start", marginTop: 1 }}>
              <option value="both">{(settings?.user_icons?.both || "🟣")} Both</option>
              <option value="nick">{(settings?.user_icons?.user1 || settings?.user_icons?.nick || "🔵")} {settings?.user1_name || "User 1"}</option>
              <option value="geri">{(settings?.user_icons?.user2 || settings?.user_icons?.geri || "🔴")} {settings?.user2_name || "User 2"}</option>
            </select>
            <button onClick={() => deleteTodo(t.id, false)}
              style={{ ...mkBtn("ghost", false), padding: "2px 4px", fontSize: 14, flexShrink: 0, lineHeight: 1 }}>×</button>
          </div>
        ))}
      </div>

      {/* Completed section */}
      {completed.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button onClick={() => setShowCompleted(s => !s)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.15em" }}>
              Completed · {completed.length}
            </span>
            <span style={{ fontSize: 10, color: C.muted, transform: showCompleted ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block", transition: "transform 0.15s" }}>▶</span>
          </button>

          {showCompleted && completed.map(t => (
            <div key={t.id} style={{ background: C.navy, borderLeft: `3px solid ${C.green}`, marginBottom: 6, padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 12, opacity: 0.65 }}>
              <button onClick={() => toggleComplete(t.id, true)}
                style={{ width: 20, height: 20, borderRadius: 2, border: `1.5px solid ${C.green}`, background: C.green, cursor: "pointer", flexShrink: 0, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.navyDark, fontSize: 12, fontWeight: 700 }}>✓</button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.4, textDecoration: "line-through", wordBreak: "break-word" }}>{t.title}</div>
                <div style={{ marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Done:</span>
                    <input type="date" defaultValue={t.completed_at ? t.completed_at.slice(0, 10) : new Date().toISOString().slice(0, 10)}
                      onChange={e => patchTodo(t.id, { completed_at: e.target.value })}
                      style={{ background: "transparent", border: "none", borderBottom: `1px solid ${C.border}`, color: C.muted, fontSize: 10, fontFamily: "inherit", colorScheme: "dark", cursor: "pointer", padding: "1px 2px" }} />
                  </div>
                  {t.responsible && (
                    <div style={{ fontSize: 12, color: C.muted }}>
                      {t.responsible === "nick" ? (settings?.user_icons?.user1 || settings?.user_icons?.nick || "🔵") : t.responsible === "geri" ? (settings?.user_icons?.user2 || settings?.user_icons?.geri || "🔴") : (settings?.user_icons?.both || "🟣")}
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => deleteTodo(t.id, true)}
                style={{ ...mkBtn("ghost", false), padding: "2px 4px", fontSize: 14, flexShrink: 0, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Weekend Planner ───────────────────────────────────────────────────────────


// Grid constants are computed inside WeekendPlanner from settings props.
const PPM = 0.8;  // px per minute — fixed visual density

const HATCH = 'repeating-linear-gradient(-45deg, rgba(0,8,20,0.72), rgba(0,8,20,0.72) 2px, rgba(0,8,20,0.45) 2px, rgba(0,8,20,0.45) 9px)';

function fmtTime(dtStr)  { return new Date(dtStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }

function WeekendPlanner({ settings }) {
  // Grid constants derived from settings
  const GRID_START = (settings?.grid_start_hour ?? 7) * 60;
  const GRID_END   = (settings?.grid_end_hour   ?? 21) * 60;
  const GRID_H     = (GRID_END - GRID_START) * PPM;

  const napEnabled = settings?.naptime_enabled !== false;
  const napStart   = settings?.naptime_start || '13:30';
  const napDur     = settings?.naptime_duration_mins ?? 120;
  const [napH, napM] = napStart.split(':').map(Number);
  const ANNA_TOP   = ((napH * 60 + napM) - GRID_START) * PPM;
  const ANNA_H     = napDur * PPM;

  const BED_EARLY_H  = (8  * 60 - GRID_START) * PPM;
  const BED_LATE_TOP = (20 * 60 - GRID_START) * PPM;
  const BED_LATE_H   = GRID_H - BED_LATE_TOP;
  const FRI_SCHOOL_TOP = BED_EARLY_H;
  const FRI_SCHOOL_H   = (16 * 60 - 8 * 60) * PPM;

  function evTop(dtStr)   { const d = new Date(dtStr); return Math.max((d.getHours()*60 + d.getMinutes() - GRID_START) * PPM, 0); }
  function evHeight(s, e) { const sm = new Date(s).getHours()*60+new Date(s).getMinutes(), em = new Date(e).getHours()*60+new Date(e).getMinutes(); return Math.max((Math.min(em,GRID_END)-Math.max(sm,GRID_START))*PPM, 20); }

  const [events,     setEvents]     = useState([]);
  const [weekend,    setWeekend]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [fetchedAt,  setFetchedAt]  = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [skipped, setSkipped] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('herrolab_weekend_skipped') || '[]')); }
    catch (_) { return new Set(); }
  });

  async function doFetch(isRefresh = false, offset = weekOffset) {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const base = new Date();
      base.setDate(base.getDate() + offset * 7);
      const today = base.toLocaleDateString('en-CA');
      const r = await fetch(`${BACKEND}/api/weekend?today=${today}`, { credentials: 'include' });
      if (reloadIf401(r)) return;
      const d = await r.json();
      if (d.error) { setError(d.error); return; }
      setWeekend({ fri: d.fri, sat: d.sat, sun: d.sun });
      setEvents(d.events || []);
      setFetchedAt(fmtRefreshed(new Date(d.cachedAt || Date.now()).toISOString()));
    } catch (e) { setError(e.message); }
    isRefresh ? setRefreshing(false) : setLoading(false);
  }

  useEffect(() => {
    doFetch(false, weekOffset);
  }, [weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleSkip(id) {
    setSkipped(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      try { localStorage.setItem('herrolab_weekend_skipped', JSON.stringify([...next])); } catch (_) {}
      return next;
    });
  }

  function dayHeader(dateStr, fallback) {
    if (!dateStr) return fallback;
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' }).toUpperCase();
  }

  if (loading) return <div style={{ padding: '48px 16px', textAlign: 'center', color: C.muted, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Loading weekend…</div>;
  if (error)   return <div style={{ maxWidth: 620, margin: '0 auto', padding: '32px 16px', color: C.red, fontSize: 13 }}>{error}</div>;

  const DAYS = ['fri', 'sat', 'sun'];
  const headers = { fri: dayHeader(weekend?.fri, 'FRI'), sat: dayHeader(weekend?.sat, 'SAT'), sun: dayHeader(weekend?.sun, 'SUN') };
  const allDay = { fri: [], sat: [], sun: [] };
  const timed  = { fri: [], sat: [], sun: [] };
  for (const ev of events) {
    if (!ev.day) continue;
    if (ev.allDay) { if (ev.isActivity) allDay[ev.day].push(ev); }
    else timed[ev.day].push(ev);
  }

  // Assign non-overlapping columns to simultaneous events so they render side-by-side
  function layoutEvents(evs) {
    const sorted = [...evs].sort((a, b) => new Date(a.start.dateTime) - new Date(b.start.dateTime));
    const colEnds = []; // colEnds[i] = end time of last event placed in column i
    const assignments = sorted.map(ev => {
      const start = new Date(ev.start.dateTime);
      const end   = new Date(ev.end.dateTime);
      let col = colEnds.findIndex(e => e <= start);
      if (col === -1) { col = colEnds.length; }
      colEnds[col] = end;
      return { ev, col };
    });
    // For each event, totalCols = widest column among all events it overlaps with + 1
    return assignments.map(({ ev, col }) => {
      const s = new Date(ev.start.dateTime), e = new Date(ev.end.dateTime);
      let maxCol = col;
      for (const { ev: o, col: oc } of assignments) {
        if (new Date(o.start.dateTime) < e && new Date(o.end.dateTime) > s) maxCol = Math.max(maxCol, oc);
      }
      return { ev, col, totalCols: maxCol + 1 };
    });
  }

  const gridStartH = GRID_START / 60;
  const gridEndH   = GRID_END   / 60;
  const hourLabels = [];
  for (let h = gridStartH; h <= gridEndH; h++) hourLabels.push({ h, label: h === 12 ? '12p' : h < 12 ? `${h}a` : `${h-12}p`, top: (h*60 - GRID_START)*PPM });

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 12px 80px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionLabel>Weekend · {headers.fri} – {headers.sun}</SectionLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {fetchedAt && <span style={{ fontSize: 10, color: C.muted, letterSpacing: '0.05em' }}>fetched {fetchedAt}</span>}
          <button onClick={() => doFetch(true)} disabled={refreshing}
            style={{ ...mkBtn('ghost', refreshing), fontSize: 10, padding: '4px 0', letterSpacing: '0.08em' }}>
            {refreshing ? 'Loading…' : '↺ Refresh'}
          </button>
          <button onClick={() => setWeekOffset(w => w - 1)} disabled={weekOffset <= 0 || loading}
            style={{ ...mkBtn('ghost', weekOffset <= 0 || loading), fontSize: 14, padding: '2px 6px', lineHeight: 1 }}>
            ←
          </button>
          <button onClick={() => setWeekOffset(w => w + 1)} disabled={loading}
            style={{ ...mkBtn('ghost', loading), fontSize: 14, padding: '2px 6px', lineHeight: 1 }}>
            →
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, overflow: 'hidden' }}>
        {/* Time axis */}
        <div style={{ width: 30, flexShrink: 0, paddingTop: 32, position: 'relative', height: GRID_H + 32 }}>
          {hourLabels.map(({ h, label, top }) => (
            <div key={h} style={{ position: 'absolute', top: top + 32 - 6, right: 3, fontSize: 8, color: C.muted, letterSpacing: '0.03em', lineHeight: 1 }}>{label}</div>
          ))}
        </div>

        {/* Day columns */}
        {DAYS.map(day => {
          const hasAllDay = allDay[day].length > 0;
          return (
            <div key={day} style={{ flex: 1, minWidth: 0, borderLeft: `1px solid ${C.border}` }}>
              {/* Header */}
              <div style={{ height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.navy, borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: C.cyan, letterSpacing: '0.1em' }}>{headers[day]}</span>
              </div>

              {/* All-day strip */}
              {hasAllDay && (
                <div style={{ borderBottom: `1px solid ${C.border}`, padding: '3px 3px', background: C.navyDark }}>
                  {allDay[day].map(ev => {
                    const skip = skipped.has(ev.id);
                    return (
                      <div key={ev.id} onClick={() => toggleSkip(ev.id)}
                        style={{ background: skip ? 'transparent' : ev.isActivity ? C.cyan : C.border, color: skip ? C.muted : ev.isActivity ? C.navyDark : C.muted, fontSize: 9, fontWeight: 700, padding: '3px 5px', marginBottom: 2, cursor: 'pointer', borderRadius: 2, opacity: skip ? 0.5 : 1, overflow: 'hidden', letterSpacing: '0.02em', border: skip ? `1px solid ${C.border}` : 'none' }}>
                        <div style={{ textDecoration: skip ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.summary}</div>
                        {skip && <div style={{ fontSize: 8, color: '#e05a5a', fontStyle: 'italic', fontWeight: 400 }}>Not attending</div>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Timed grid */}
              <div style={{ position: 'relative', height: GRID_H, overflow: 'hidden' }}>
                {/* Hour lines */}
                {hourLabels.map(({ h, top }) => (
                  <div key={h} style={{ position: 'absolute', top, left: 0, right: 0, borderTop: `1px solid ${C.border}`, opacity: 0.35 }} />
                ))}

                {/* Bedtime — before 8am */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: BED_EARLY_H, background: HATCH, zIndex: 1, pointerEvents: 'none', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 3 }}>
                  <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Bedtime</span>
                </div>

                {/* Bedtime — after 8pm */}
                <div style={{ position: 'absolute', top: BED_LATE_TOP, left: 0, right: 0, height: BED_LATE_H, background: HATCH, zIndex: 1, pointerEvents: 'none', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 3 }}>
                  <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Bedtime</span>
                </div>

                {/* Friday school block — 8am–4pm */}
                {day === 'fri' && (
                  <div style={{ position: 'absolute', top: FRI_SCHOOL_TOP, left: 0, right: 0, height: FRI_SCHOOL_H, background: HATCH, zIndex: 1, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>School day</span>
                  </div>
                )}

                {/* Weekend naptime (Sat + Sun only, when enabled) */}
                {napEnabled && (day === 'sat' || day === 'sun') && ANNA_TOP >= 0 && (
                  <div style={{ position: 'absolute', top: ANNA_TOP, left: 0, right: 0, height: ANNA_H, background: 'rgba(43,238,253,0.05)', borderTop: '1px dashed rgba(43,238,253,0.25)', borderBottom: '1px dashed rgba(43,238,253,0.25)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <span style={{ fontSize: 8, color: C.cyan, opacity: 0.5, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Naptime</span>
                  </div>
                )}

                {/* Events */}
                {timed[day].length === 0 && (
                  <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, textAlign: 'center', color: C.muted, fontSize: 10, letterSpacing: '0.05em', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 3 }}>—</div>
                )}
                {layoutEvents(timed[day]).map(({ ev, col, totalCols }) => {
                  const skip = skipped.has(ev.id);
                  const top  = evTop(ev.start.dateTime);
                  const h    = evHeight(ev.start.dateTime, ev.end.dateTime);
                  const act  = ev.isActivity;
                  const pct  = 100 / totalCols;
                  return (
                    <div key={ev.id} onClick={() => toggleSkip(ev.id)}
                      style={{ position: 'absolute', top, left: `calc(${col * pct}% + 2px)`, width: `calc(${pct}% - 4px)`, height: h, zIndex: 3, cursor: 'pointer', overflow: 'hidden', borderRadius: 2, padding: '2px 4px', background: skip ? C.navyDark : act ? '#1a3d6e' : C.navy, border: `1px solid ${skip ? C.border : act ? '#4a7ab5' : C.border}`, borderLeft: `3px solid ${skip ? C.border : act ? '#5b9bd5' : C.muted}`, opacity: skip ? 0.5 : 1 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: skip ? C.muted : C.offWhite, lineHeight: 1.25, textDecoration: skip ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.summary}</div>
                      {skip
                        ? <div style={{ fontSize: 8, color: '#e05a5a', lineHeight: 1.2, fontStyle: 'italic', letterSpacing: '0.02em' }}>Not attending</div>
                        : h > 28 && (
                          <div style={{ fontSize: 8, color: C.muted, lineHeight: 1.2 }}>
                            {fmtTime(ev.start.dateTime)}
                            {ev.location && h > 48 && (
                              <> · {ev.location}</>
                            )}
                            {ev.location && h > 60 && (
                              <div style={{ marginTop: 2 }}>
                                <a href={`https://maps.google.com/?q=${encodeURIComponent(ev.location)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                  style={{ fontSize: 7, color: C.cyan, textDecoration: 'none', background: 'rgba(43,174,253,0.12)', padding: '1px 4px', borderRadius: 2, letterSpacing: '0.04em' }}>
                                  ↗ Maps
                                </a>
                              </div>
                            )}
                          </div>
                        )
                      }
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Calendar Manager ──────────────────────────────────────────────────────────

const SYNC_INTERVAL_DAYS = 3;
const SYNC_LS_KEY = 'herrolab_last_travel_sync';

function TravelPage({ settings }) {
  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "32px 16px 80px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>✈️ Travel</div>
        <div style={{ fontSize: 'clamp(16px,4vw,20px)', fontWeight: 400, color: C.white, fontFamily: "'Georgia', serif" }}>Trips & Conflict Detection</div>
        <div style={{ width: 32, height: 2, background: C.cyanBright, marginTop: 10 }} />
      </div>
      <TravelOrganizer settings={settings} />
    </div>
  );
}

function EventsPage({ settings }) {
  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "32px 16px 80px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>📅 Events</div>
        <div style={{ fontSize: 'clamp(16px,4vw,20px)', fontWeight: 400, color: C.white, fontFamily: "'Georgia', serif" }}>Add & Browse Calendar Events</div>
        <div style={{ width: 32, height: 2, background: C.cyanBright, marginTop: 10 }} />
      </div>
      <EventManager settings={settings} />
    </div>
  );
}

function TravelOrganizer({ settings }) {
  const [syncing, setSyncing]         = useState(false);
  const [checking, setChecking]       = useState(false);
  const [activity, setActivity]       = useState([]);
  const [conflicts, setConflicts]     = useState([]);
  const [allTravel, setAllTravel]     = useState([]);
  const [lastChecked, setLastChecked] = useState(null);
  const [lastSynced, setLastSynced]   = useState(() => { const raw = localStorage.getItem(SYNC_LS_KEY); return raw ? fmtRefreshed(new Date(parseInt(raw)).toISOString()) : null; });
  const [autoSynced, setAutoSynced]   = useState(false);
  const logRef = useRef(null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [activity]);

  useEffect(() => {
    // Load cached travel on mount
    fetch(`${BACKEND}/api/digest`, { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        if (d.conflicts) {
          setAllTravel(d.conflicts.travel || []);
          setConflicts(d.conflicts.conflicts || []);
          setLastChecked(fmtRefreshed(new Date(d.conflicts.cachedAt).toISOString()));
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    // Auto-sync if > 3 days since last sync
    if (autoSynced) return;
    const last = localStorage.getItem(SYNC_LS_KEY);
    if (!last || (Date.now() - parseInt(last)) > SYNC_INTERVAL_DAYS * 86400000) {
      setAutoSynced(true);
      runSync(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function log(type, lines) {
    const msgs = Array.isArray(lines) ? lines : [lines];
    const t = ts();
    setActivity(a => [...a, ...msgs.map(m => ({ type, message: m, time: t, id: Date.now() + Math.random() }))]);
  }

  async function runSync(silent = false) {
    setSyncing(true);
    if (!silent) log("info", "Syncing travel from personal calendar…");
    try {
      const res = await fetch(`${BACKEND}/api/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ messages: [{ role: "user", content: todayStr() + "sync travel" }] }),
      });
      const data = await res.json();
      const reply = (data.content || []).filter(b => b.type === "text").map(b => b.text).join(" ").trim();
      const lines = reply.replace("[AWAITING_RESPONSE]", "").trim().split("\n")
        .map(l => l.trim().replace(/^#{1,6}\s+/, "").replace(/\*\*(.*?)\*\*/g, "$1").replace(/^[-–—]\s+/, "• "))
        .filter(Boolean);
      log("info", lines.length ? lines : ["Sync complete."]);
    } catch (e) { log("error", "Error: " + e.message); }
    const now = Date.now();
    localStorage.setItem(SYNC_LS_KEY, String(now));
    setLastSynced(fmtRefreshed(new Date(now).toISOString()));
    await runCheck();
    setSyncing(false);
  }

  async function runCheck() {
    setChecking(true);
    try {
      const res = await fetch(`${BACKEND}/api/conflicts`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ messages: [{ role: "user", content: todayStr() }] }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setConflicts(data.conflicts || []);
      setAllTravel(data.travel || []);
      setLastChecked(fmtRefreshed(new Date(data.cachedAt || Date.now()).toISOString()));
    } catch (e) { log("error", e.message); }
    setChecking(false);
  }

  const busy = syncing || checking;
  const ui = settings?.user_icons || {};
  const members = settings?.members || [];
  const m1 = members[0]?.display_name || members[0]?.name || 'User 1';
  const m2 = members[1]?.display_name || members[1]?.name || 'User 2';

  return (
    <div>
      {/* Conflicts — always visible bar */}
      <div style={{ marginBottom: 20 }}>
        <SectionLabel>⚠️ Travel Conflicts</SectionLabel>
        <div style={{ border: `1px solid ${conflicts.length > 0 ? C.cyanBright : C.border}`, minHeight: 44 }}>
          {conflicts.length > 0
            ? conflicts.map((c, i) => <TravelRow key={i} c={c} settings={settings} />)
            : <p style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '12px 16px', margin: 0 }}>
                {lastChecked ? '✓ No conflicts detected' : 'Run a check to detect travel conflicts'}
              </p>
          }
        </div>
      </div>

      {/* Status bar */}
      <div style={{ background: C.navy, border: `1px solid ${C.border}`, padding: '12px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 12, color: C.muted }}>
          {lastSynced
            ? <span>Auto-sync: <span style={{ color: C.offWhite }}>every {SYNC_INTERVAL_DAYS} days</span> · Last synced: <span style={{ color: C.offWhite }}>{lastSynced}</span></span>
            : <span style={{ color: C.muted }}>Auto-syncing on first load…</span>}
        </div>
        <button onClick={() => runSync(false)} disabled={busy}
          style={{ ...mkBtn('ghost', busy), fontSize: 11, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', animation: syncing ? 'spin 1.5s linear infinite' : 'none' }}>✈</span>
          {syncing ? 'Syncing…' : 'Force Sync'}
        </button>
      </div>

      {/* Upcoming travel */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <SectionLabel>Upcoming Travel · Next 90 Days</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {lastChecked && <span style={{ fontSize: 10, color: C.muted }}>checked {lastChecked}</span>}
            <button onClick={() => runCheck()} disabled={busy}
              style={{ ...mkBtn('ghost', busy), fontSize: 10, padding: '4px 0' }}>
              {checking ? 'Loading…' : '↺ Refresh'}
            </button>
          </div>
        </div>
        {allTravel.length === 0 && !lastChecked ? (
          <button onClick={() => runCheck()} disabled={busy}
            style={{ ...mkBtn(checking ? 'cyan' : 'primary', busy && !checking), width: '100%', padding: '16px', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ display: 'inline-block', animation: checking ? 'spin 1.5s linear infinite' : 'none' }}>✦</span>
            {checking ? 'Loading…' : 'Load Upcoming Travel'}
          </button>
        ) : null}
        <div style={{ border: `1px solid ${C.border}`, minHeight: 80 }}>
          {allTravel.length === 0
            ? <p style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '28px 16px', letterSpacing: '0.03em' }}>
                {lastChecked ? 'No upcoming travel found.' : 'Loading…'}
              </p>
            : allTravel.map((c, i) => <TravelRow key={i} c={c} settings={settings} />)}
        </div>
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: 11, color: C.muted }}>
          <span>{ui.user1 || ui.nick || '🔵'} {m1}</span>
          <span>{ui.user2 || ui.geri || '🔴'} {m2}</span>
          <span>{ui.both || '🟣'} Together</span>
          <span>{ui.conflict || '⚠️'} Conflict</span>
        </div>
      </div>

      {/* Activity log */}
      {activity.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <SectionLabel>Sync Log</SectionLabel>
            <button onClick={() => { setActivity([]); }}
              style={{ ...mkBtn('ghost', false), fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Clear</button>
          </div>
          <div ref={logRef} style={{ border: `1px solid ${C.border}`, maxHeight: 200, overflowY: 'auto', padding: '8px 0' }}>
            {activity.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 14px', borderLeft: `2px solid ${COLORS[item.type] || C.border}` }}>
                <span style={{ color: COLORS[item.type] || C.muted, fontSize: 12, flexShrink: 0 }}>{ICONS[item.type] || '·'}</span>
                <span style={{ color: C.offWhite, fontSize: 13, flex: 1, lineHeight: 1.5, wordBreak: 'break-word' }}>{item.message}</span>
                <span style={{ color: C.muted, fontSize: 10, flexShrink: 0, paddingTop: 2 }}>{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EventManager({ settings }) {
  const [processing, setProcessing]   = useState(false);
  const [textInput, setTextInput]     = useState("");
  const [updateInput, setUpdateInput] = useState("");
  const [replyInput, setReplyInput]   = useState("");
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [history, setHistory]         = useState([]);
  const [activity, setActivity]       = useState([]);
  const [roster, setRoster]           = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterFilter, setRosterFilter] = useState('all'); // 'all' | person name
  const logRef   = useRef(null);
  const replyRef = useRef(null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [activity]);
  useEffect(() => { if (awaitingReply && replyRef.current) replyRef.current.focus(); }, [awaitingReply]);
  useEffect(() => { loadRoster(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadRoster() {
    setRosterLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/events/roster?days=90`, { credentials: 'include' });
      if (reloadIf401(r)) return;
      const data = await r.json();
      setRoster(data.events || []);
    } catch (_) { setRoster([]); }
    setRosterLoading(false);
  }

  function log(type, lines) {
    const msgs = Array.isArray(lines) ? lines : [lines];
    const t = ts();
    setActivity(a => [...a, ...msgs.map(m => ({ type, message: m, time: t, id: Date.now() + Math.random() }))]);
  }

  async function callBackend(messages) {
    const res = await fetch(`${BACKEND}/api/chat`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify({ messages }),
    });
    return res.json();
  }

  function processResp(data, newHist) {
    const reply = (data.content || []).filter(b => b.type === "text").map(b => b.text).join(" ").trim();
    const isQ   = reply.includes("[AWAITING_RESPONSE]");
    const clean = reply.replace("[AWAITING_RESPONSE]", "").trim();
    setHistory([...newHist, { role: "assistant", content: reply }]);
    const lines = clean.split("\n")
      .map(l => l.trim().replace(/^#{1,6}\s+/, "").replace(/\*\*(.*?)\*\*/g, "$1").replace(/^[-–—]\s+/, "• "))
      .filter(Boolean);
    log(isQ ? "question" : "info", lines.length ? lines : ["Done."]);
    setAwaitingReply(isQ);
    if (!isQ) loadRoster(); // refresh roster after changes
  }

  async function send(text, hist) {
    const newHist = [...hist, { role: "user", content: todayStr() + text }];
    setHistory(newHist);
    try {
      const data = await callBackend(newHist);
      processResp(data, newHist);
    } catch (e) { log("error", "Error: " + e.message); }
  }

  async function submitAdd() {
    if (!textInput.trim()) return;
    const t = textInput.trim(); setTextInput(""); setProcessing(true);
    log("user", "Add: " + t); await send(t, history); setProcessing(false);
  }

  async function submitUpdate() {
    if (!updateInput.trim()) return;
    const t = updateInput.trim(); setUpdateInput(""); setProcessing(true);
    log("user", "Update: " + t); await send(t, history); setProcessing(false);
  }

  async function submitReply() {
    if (!replyInput.trim()) return;
    const r = replyInput.trim(); setReplyInput(""); setAwaitingReply(false); setProcessing(true);
    log("user", r); await send(r, history); setProcessing(false);
  }

  const busy = processing;

  // Build roster people list for filter
  const rosterPeople = roster ? [...new Set(roster.map(e => e.person).filter(p => p && p !== 'family'))].sort() : [];
  const filteredRoster = roster ? (rosterFilter === 'all' ? roster : roster.filter(e => e.person === rosterFilter || (rosterFilter === 'family' && e.person === 'family'))) : [];

  function fmtEventTime(e) {
    if (e.allDay) return 'All day';
    const dt = e.start.dateTime;
    if (!dt) return '';
    try { return new Date(dt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
    catch (_) { return ''; }
  }

  // Group roster by date
  const rosterByDate = {};
  for (const evt of filteredRoster) {
    const d = evt.start.date || evt.start.dateTime?.slice(0, 10);
    if (!d) continue;
    if (!rosterByDate[d]) rosterByDate[d] = [];
    rosterByDate[d].push(evt);
  }
  const rosterDates = Object.keys(rosterByDate).sort();

  return (
    <div>
      {/* Add Event */}
      <div style={{ marginBottom: 28 }}>
        <SectionLabel>Add Event</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input type="text" placeholder='e.g. "Dentist Thursday at 3pm" or "Maya piano lesson every Wednesday at 4pm"'
            value={textInput}
            onChange={e => setTextInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !busy && submitAdd()}
            disabled={busy} style={{ ...inputBase, width: "100%", opacity: busy ? 0.5 : 1 }} />
          <button onClick={submitAdd} disabled={busy || !textInput.trim()}
            style={{ ...mkBtn("primary", busy || !textInput.trim()), padding: "14px", fontSize: 13, width: "100%" }}>
            Add Event
          </button>
        </div>
      </div>

      <Divider />

      {/* Update Event */}
      <div style={{ marginBottom: 28 }}>
        <SectionLabel>Update Event</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <textarea placeholder='e.g. "Change Friday dinner to 7pm" or "Remove duplicate Cancun flight"'
            value={updateInput} onChange={e => setUpdateInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && !busy && (e.preventDefault(), submitUpdate())}
            disabled={busy} rows={3}
            style={{ ...inputBase, resize: "none", width: "100%", opacity: busy ? 0.5 : 1 }} />
          <button onClick={submitUpdate} disabled={busy || !updateInput.trim()}
            style={{ ...mkBtn("primary", busy || !updateInput.trim()), padding: "14px", fontSize: 13, width: "100%" }}>
            Update Event
          </button>
        </div>
      </div>

      {/* Activity log */}
      {activity.length > 0 && (
        <>
          <Divider />
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <SectionLabel>Activity Log</SectionLabel>
              <button onClick={() => { setActivity([]); setHistory([]); setAwaitingReply(false); }}
                style={{ ...mkBtn("ghost", false), fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>Clear</button>
            </div>
            <div ref={logRef} style={{ border: `1px solid ${C.border}`, maxHeight: 200, overflowY: "auto", padding: "8px 0" }}>
              {activity.map(item => (
                <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 14px", borderLeft: `2px solid ${COLORS[item.type] || C.border}` }}>
                  <span style={{ color: COLORS[item.type] || C.muted, fontSize: 12, flexShrink: 0 }}>{ICONS[item.type] || '·'}</span>
                  <span style={{ color: item.type === "user" ? C.muted : C.offWhite, fontSize: 13, flex: 1, lineHeight: 1.5, fontStyle: item.type === "user" ? "italic" : "normal", wordBreak: "break-word" }}>{item.message}</span>
                  <span style={{ color: C.muted, fontSize: 10, flexShrink: 0, paddingTop: 2 }}>{item.time}</span>
                </div>
              ))}
            </div>
            {awaitingReply && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                <input ref={replyRef} type="text" placeholder="Reply…" value={replyInput}
                  onChange={e => setReplyInput(e.target.value)} onKeyDown={e => e.key === "Enter" && submitReply()}
                  disabled={processing} style={{ ...inputBase, width: "100%", borderColor: C.cyanBright }} />
                <button onClick={submitReply} disabled={processing || !replyInput.trim()}
                  style={{ ...mkBtn("primary", processing || !replyInput.trim()), padding: "14px", fontSize: 13, width: "100%" }}>
                  Send Reply
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <Divider />

      {/* 90-day roster */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <SectionLabel>Upcoming 90 Days</SectionLabel>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Person filter */}
            {rosterPeople.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {['all', ...rosterPeople, 'family'].map(p => (
                  <button key={p} onClick={() => setRosterFilter(p)}
                    style={{ background: rosterFilter === p ? C.cyan : 'transparent', border: `1px solid ${rosterFilter === p ? C.cyan : C.border}`, color: rosterFilter === p ? C.navyDark : C.muted, padding: '3px 9px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', fontWeight: rosterFilter === p ? 700 : 400 }}>
                    {p === 'all' ? 'All' : p === 'family' ? '🟣 Family' : p}
                  </button>
                ))}
              </div>
            )}
            <button onClick={loadRoster} disabled={rosterLoading}
              style={{ ...mkBtn('ghost', rosterLoading), fontSize: 10, padding: '3px 8px' }}>
              {rosterLoading ? '…' : '↺'}
            </button>
          </div>
        </div>

        {rosterLoading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: C.muted, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Loading events…</div>
        ) : rosterDates.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: C.muted, fontSize: 13 }}>
            No upcoming events found in the shared calendar.
          </div>
        ) : (
          <div>
            {rosterDates.map(date => (
              <div key={date} style={{ marginBottom: 2 }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '8px 0 4px', fontWeight: 700 }}>
                  {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
                {rosterByDate[date].map((evt, i) => (
                  <div key={evt.id} style={{ background: C.navy, border: `1px solid ${C.border}`, padding: '10px 16px', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 15, flexShrink: 0 }}>{evt.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: C.white, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{evt.summary}</div>
                      {evt.location && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>📍 {evt.location}</div>}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, flexShrink: 0, textAlign: 'right' }}>
                      {fmtEventTime(evt)}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settings Manager ──────────────────────────────────────────────────────────

function EmojiPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ fontSize: 20, background: C.navyDark, border: `1px solid ${C.border}`, padding: '4px 8px', cursor: 'pointer', minWidth: 44 }}>
        {value || '?'}
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 100, top: '110%', left: 0, background: C.navy, border: `1px solid ${C.border}`, padding: 8, display: 'flex', flexWrap: 'wrap', gap: 4, width: 240 }}>
          {EMOJI_PALETTE.map(e => (
            <button key={e} type="button" onClick={() => { onChange(e); setOpen(false); }}
              style={{ fontSize: 18, background: value === e ? C.border : 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, lineHeight: 1.4 }}>
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const TIMEZONES = [
  'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
  'America/Phoenix','America/Anchorage','Pacific/Honolulu',
  'Europe/London','Europe/Paris','Europe/Berlin','Europe/Amsterdam',
  'Asia/Tokyo','Asia/Shanghai','Asia/Kolkata','Australia/Sydney',
];

function SettingsManager({ settings, meData, onSaved }) {
  const [tab, setTab] = useState('personal'); // 'personal' | 'shared'
  const [personal, setPersonal] = useState(null);
  const [shared, setShared] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [newCovLabel, setNewCovLabel] = useState('');
  const [scraping, setScraping] = useState({}); // {childIdx: bool}
  const [showCalDates, setShowCalDates] = useState({}); // {childIdx: bool} — expand parsed dates list
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');
  const [unlinking, setUnlinking] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleDisconnecting, setGoogleDisconnecting] = useState(false);
  const [calPickerOpen, setCalPickerOpen] = useState(false);
  const [calPickerList, setCalPickerList] = useState(null);
  const [calPickerLoading, setCalPickerLoading] = useState(false);

  useEffect(() => {
    if (settings && !personal) {
      setPersonal({
        display_name: settings.display_name || settings.user1_name || '',
        personal_cal_id: settings.personal_cal_id || '',
        email_recipients: settings.email_recipients || [],
        send_day: settings.send_day ?? 0,
        send_hour: settings.send_hour ?? 15,
      });
    }
    if (settings && !shared) {
      setShared({
        family_name: settings.family_name || 'Family',
        user_icons: settings.user_icons || { user1: '🔵', user2: '🔴', both: '🟣', conflict: '⚠️' },
        coverage_options: settings.coverage_options || [],
        children: settings.children || [],
        timezone: settings.timezone || 'America/Chicago',
        shared_cal_id: settings.shared_cal_id || '',
        grid_start_hour: settings.grid_start_hour ?? 7,
        grid_end_hour: settings.grid_end_hour ?? 21,
        naptime_enabled: settings.naptime_enabled !== false,
        naptime_start: settings.naptime_start || '13:30',
        naptime_duration_mins: settings.naptime_duration_mins ?? 120,
        show_weekend_meals: settings.show_weekend_meals ?? false,
        kid_lunches: settings.kid_lunches || [],
      });
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!personal || !shared) return (
    <div style={{ padding: '48px 16px', textAlign: 'center', color: C.muted, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Loading settings…</div>
  );

  function setP(key, val) { setPersonal(d => ({ ...d, [key]: val })); }
  function setS(key, val) { setShared(d => ({ ...d, [key]: val })); }
  function setIcon(who, val) { setShared(d => ({ ...d, user_icons: { ...d.user_icons, [who]: val } })); }
  function setCovOpt(idx, field, val) {
    setShared(d => {
      const opts = [...(d.coverage_options || [])];
      opts[idx] = { ...opts[idx], [field]: val };
      return { ...d, coverage_options: opts };
    });
  }
  function removeCovOpt(idx) { setShared(d => ({ ...d, coverage_options: d.coverage_options.filter((_, i) => i !== idx) })); }
  function moveCovOpt(fromIdx, toIdx) {
    setShared(d => {
      const opts = [...(d.coverage_options || [])];
      const [moved] = opts.splice(fromIdx, 1);
      opts.splice(toIdx, 0, moved);
      return { ...d, coverage_options: opts };
    });
  }
  function addCovOpt() {
    if (!newCovLabel.trim()) return;
    const value = newCovLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setShared(d => ({ ...d, coverage_options: [...(d.coverage_options || []), { value: value || 'custom_' + Date.now(), label: newCovLabel.trim(), emoji: '📌' }] }));
    setNewCovLabel('');
  }
  function setChild(ci, field, val) {
    setShared(d => {
      const children = [...(d.children || [])];
      children[ci] = { ...children[ci], [field]: val };
      return { ...d, children };
    });
  }
  function addChild() {
    setShared(d => ({ ...d, children: [...(d.children || []), { id: 'child_' + Date.now(), name: '', school_cal_url: '', no_school_keywords: [], summer_weeks: '', school_cal_type: null, school_cal_dates: null }] }));
  }
  function removeChild(ci) { setShared(d => ({ ...d, children: d.children.filter((_, i) => i !== ci) })); }

  async function scrapeChild(ci) {
    const url = shared.children[ci]?.school_cal_url;
    if (!url) return;
    setScraping(s => ({ ...s, [ci]: true }));
    try {
      const res = await fetch(`${BACKEND}/api/settings/scrape-school-cal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ url }),
      });
      const data = await res.json();
      const updatedChildren = [...(shared.children || [])];
      if (data.type === 'ics' && data.keywords?.length) {
        updatedChildren[ci] = { ...updatedChildren[ci], no_school_keywords: data.keywords, school_cal_type: 'ics', school_cal_dates: data.dates?.length ? data.dates : null };
      } else if ((data.type === 'pdf' || data.type === 'webpage') && data.dates?.length) {
        updatedChildren[ci] = { ...updatedChildren[ci], school_cal_dates: data.dates, school_cal_type: data.type };
      } else if (data.error) {
        alert(`Calendar parse failed: ${data.error}`);
        setScraping(s => ({ ...s, [ci]: false }));
        return;
      }
      const updatedShared = { ...shared, children: updatedChildren };
      setShared(updatedShared);
      await fetch(`${BACKEND}/api/settings/shared`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(updatedShared),
      });
    } catch (_) {}
    setScraping(s => ({ ...s, [ci]: false }));
  }

  function calScrapeLabel(url, scraping) {
    if (scraping) return 'Analyzing…';
    if (!url) return 'Detect';
    const l = url.toLowerCase();
    if (l.includes('.ics') || l.includes('ical') || l.startsWith('webcal') || l.includes('calendar.google')) return 'Import Keywords';
    if (l.includes('.pdf')) return 'Parse PDF';
    return 'Parse Calendar';
  }

  async function connectGoogle() {
    setGoogleConnecting(true);
    try {
      const r = await fetch(`${BACKEND}/api/auth-google-url`, { credentials: 'include' });
      if (r.status === 401) { window.location.reload(); return; }
      const d = await r.json();
      if (d.url) window.location.href = d.url;
    } catch (_) {}
    setGoogleConnecting(false);
  }

  async function openCalPicker() {
    setCalPickerOpen(true);
    if (calPickerList) return; // already loaded
    setCalPickerLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/calendars/list`, { credentials: 'include' });
      const d = await r.json();
      setCalPickerList(d.calendars || []);
    } catch (_) { setCalPickerList([]); }
    setCalPickerLoading(false);
  }

  async function disconnectGoogle() {
    setGoogleDisconnecting(true);
    try {
      const r = await fetch(`${BACKEND}/api/disconnect-google`, { method: 'POST', credentials: 'include' });
      if (r.ok) {
        onSaved({ ...settings, hasGoogle: false });
      }
    } catch (_) {}
    setGoogleDisconnecting(false);
  }

  async function savePersonal() {
    setSaving(true); setSaveStatus(null);
    try {
      const res = await fetch(`${BACKEND}/api/settings/personal`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(personal),
      });
      if (res.status === 401) { window.location.reload(); return; }
      const data = await res.json();
      if (res.ok && data.ok) {
        setSaveStatus('saved');
        onSaved({ ...settings, ...personal });
        setTimeout(() => setSaveStatus(null), 3000);
      } else setSaveStatus('error');
    } catch (_) { setSaveStatus('error'); }
    setSaving(false);
  }

  async function saveShared() {
    setSaving(true); setSaveStatus(null);
    try {
      const res = await fetch(`${BACKEND}/api/settings/shared`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(shared),
      });
      if (res.status === 401) { window.location.reload(); return; }
      const data = await res.json();
      if (res.ok && data.ok) {
        setSaveStatus('saved');
        onSaved({ ...settings, ...shared });
        setTimeout(() => setSaveStatus(null), 3000);
      } else setSaveStatus('error');
    } catch (_) { setSaveStatus('error'); }
    setSaving(false);
  }

  async function unlinkPartner() {
    if (!window.confirm('Are you sure you want to disconnect from your partner? You will lose access to shared data.')) return;
    setUnlinking(true);
    try {
      const res = await fetch(`${BACKEND}/api/unlink-partner`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok) { onSaved({}); window.location.reload(); }
    } catch (_) {}
    setUnlinking(false);
  }

  async function sendInvite() {
    if (!inviteEmail.trim()) return;
    setInviting(true); setInviteMsg('');
    try {
      const res = await fetch(`${BACKEND}/api/invite-partner`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.ok) { setInviteMsg('Invite sent!'); setInviteEmail(''); }
      else setInviteMsg(data.error || 'Failed to send invite.');
    } catch (_) { setInviteMsg('Failed to send invite.'); }
    setInviting(false);
  }

  const row = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 };
  const lbl = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', minWidth: 140, flexShrink: 0 };
  const smallInput = { ...inputBase, padding: '8px 12px', fontSize: 13, flex: 1 };
  const members = meData?.members || [];
  const partner = members.find(m => m.id !== meData?.userId);

  return (
    <div style={{ maxWidth: 620, margin: '0 auto', padding: '32px 16px 80px' }}>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderBottom: `1px solid ${C.border}` }}>
        {['personal', 'shared'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ background: 'none', border: 'none', borderBottom: `2px solid ${tab === t ? C.cyanBright : 'transparent'}`, padding: '10px 20px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: tab === t ? C.cyanBright : C.muted, cursor: 'pointer', fontFamily: 'inherit', marginBottom: -1 }}>
            {t === 'personal' ? 'My Settings' : 'Family Settings'}
          </button>
        ))}
      </div>

      {tab === 'personal' && (
        <div>
          <SectionLabel>My Profile</SectionLabel>
          <div style={row}>
            <span style={lbl}>Display name</span>
            <input value={personal.display_name} onChange={e => setP('display_name', e.target.value)} style={smallInput} />
          </div>

          <Divider />

          <SectionLabel>Calendar</SectionLabel>
          <div style={row}>
            <span style={lbl}>Personal cal ID</span>
            <input value={personal.personal_cal_id} onChange={e => setP('personal_cal_id', e.target.value)} style={smallInput} placeholder="your-calendar-id@gmail.com" />
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, lineHeight: 1.6, marginTop: -8 }}>
            Your personal Google Calendar ID. Find it in Google Calendar Settings → your calendar → "Calendar ID".
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {!meData?.hasGoogle ? (
                <button onClick={connectGoogle} disabled={googleConnecting}
                  style={{ ...mkBtn('primary', googleConnecting), padding: '8px 16px', fontSize: 11 }}>
                  {googleConnecting ? 'Redirecting…' : 'Connect Google Calendar →'}
                </button>
              ) : (
                <>
                  <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>✓ Google Calendar connected</span>
                  <button onClick={disconnectGoogle} disabled={googleDisconnecting}
                    style={{ ...mkBtn('danger', googleDisconnecting), padding: '6px 12px', fontSize: 10 }}>
                    {googleDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </>
              )}
            </div>
          </div>

          <Divider />

          <SectionLabel>Email & Auto-Send</SectionLabel>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', marginBottom: 8 }}>
              <div onClick={() => {
                const myEmail = meData?.email || '';
                const enabled = (personal.email_recipients || []).length > 0;
                setP('email_recipients', enabled ? [] : (myEmail ? [myEmail] : []));
              }}
                style={{ width: 40, height: 22, borderRadius: 11, background: (personal.email_recipients || []).length > 0 ? C.cyan : C.border, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: 3, left: (personal.email_recipients || []).length > 0 ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: C.white, transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: 13, color: (personal.email_recipients || []).length > 0 ? C.offWhite : C.muted }}>
                Send me the weekly digest email
              </span>
            </label>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>Sent to {meData?.email || 'your email'}</div>
          </div>
          <div style={row}>
            <span style={lbl}>Send day</span>
            <select value={personal.send_day ?? 0} onChange={e => setP('send_day', Number(e.target.value))} style={{ ...smallInput, flex: '0 0 auto' }}>
              {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d,i) => (
                <option key={i} value={i}>{d}</option>
              ))}
            </select>
          </div>
          <div style={row}>
            <span style={lbl}>Send hour</span>
            <select value={personal.send_hour ?? 15} onChange={e => setP('send_hour', Number(e.target.value))} style={{ ...smallInput, flex: '0 0 auto' }}>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h-12} PM`}</option>
              ))}
            </select>
          </div>

          <Divider />

          {/* Partner pairing */}
          <SectionLabel>Partner</SectionLabel>
          {partner ? (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: C.offWhite, marginBottom: 12 }}>
                Paired with <strong>{partner.display_name || partner.name}</strong>
                {partner.has_google && <span style={{ color: C.green, marginLeft: 8, fontSize: 11 }}>✓ Google connected</span>}
              </div>
              <button onClick={unlinkPartner} disabled={unlinking}
                style={{ ...mkBtn('danger', unlinking), padding: '8px 16px', fontSize: 11 }}>
                {unlinking ? 'Disconnecting…' : 'Disconnect from partner'}
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.6 }}>
                Invite your partner to join this dashboard. They'll sign up and pair with your account.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="email" placeholder="Partner's email…" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendInvite()}
                  style={{ ...smallInput, flex: 1 }} />
                <button onClick={sendInvite} disabled={inviting || !inviteEmail.trim()}
                  style={{ ...mkBtn('primary', inviting || !inviteEmail.trim()), padding: '8px 16px', fontSize: 12 }}>
                  {inviting ? 'Sending…' : 'Invite'}
                </button>
              </div>
              {inviteMsg && <div style={{ fontSize: 12, color: inviteMsg.includes('sent') ? C.green : C.red, marginTop: 8 }}>{inviteMsg}</div>}
            </div>
          )}

          <Divider />

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={savePersonal} disabled={saving}
              style={{ ...mkBtn('primary', saving), padding: '16px 32px', fontSize: 13 }}>
              {saving ? 'Saving…' : 'Save My Settings'}
            </button>
            {saveStatus === 'saved' && <span style={{ fontSize: 12, color: C.green }}>✓ Saved</span>}
            {saveStatus === 'error' && <span style={{ fontSize: 12, color: C.red }}>✗ Failed to save</span>}
          </div>
        </div>
      )}

      {tab === 'shared' && (
        <div>
          <SectionLabel>Family</SectionLabel>
          <div style={row}>
            <span style={lbl}>Family name</span>
            <input value={shared.family_name} onChange={e => setS('family_name', e.target.value)} style={smallInput} placeholder="Your Family Name" />
          </div>

          <Divider />

          <SectionLabel>User Icons</SectionLabel>
          <div style={row}>
            <span style={lbl}>{members[0]?.display_name || 'User 1'}</span>
            <EmojiPicker value={shared.user_icons?.user1 || shared.user_icons?.nick} onChange={v => setIcon('user1', v)} />
          </div>
          <div style={row}>
            <span style={lbl}>{members[1]?.display_name || 'User 2'}</span>
            <EmojiPicker value={shared.user_icons?.user2 || shared.user_icons?.geri} onChange={v => setIcon('user2', v)} />
          </div>
          <div style={row}>
            <span style={lbl}>Both icon</span>
            <span style={{ fontSize: 12, color: C.muted, flex: 1 }}>Shared tasks/travel</span>
            <EmojiPicker value={shared.user_icons?.both} onChange={v => setIcon('both', v)} />
          </div>
          <div style={row}>
            <span style={lbl}>Conflict icon</span>
            <span style={{ fontSize: 12, color: C.muted, flex: 1 }}>Travel overlap</span>
            <EmojiPicker value={shared.user_icons?.conflict} onChange={v => setIcon('conflict', v)} />
          </div>

          <Divider />

          <SectionLabel>Shared Calendar</SectionLabel>
          <div style={row}>
            <span style={lbl}>Timezone</span>
            <select value={shared.timezone} onChange={e => setS('timezone', e.target.value)} style={{ ...smallInput }}>
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>)}
              {!TIMEZONES.includes(shared.timezone) && <option value={shared.timezone}>{shared.timezone}</option>}
            </select>
          </div>
          <div style={{ ...row, flexWrap: 'wrap', rowGap: 8 }}>
            <span style={lbl}>Shared cal ID</span>
            <input value={shared.shared_cal_id} onChange={e => setS('shared_cal_id', e.target.value)} style={{ ...smallInput, minWidth: 0, flex: '1 1 180px' }} placeholder="family-calendar-id@group.calendar.google.com" />
            <button type="button" onClick={openCalPicker}
              style={{ ...mkBtn('ghost', false), padding: '8px 12px', fontSize: 11, flexShrink: 0, whiteSpace: 'nowrap' }}>
              Browse…
            </button>
          </div>
          {calPickerOpen && (
            <div style={{ background: C.navyDark, border: `1px solid ${C.border}`, marginBottom: 12, marginTop: -4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 10, color: C.cyan, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Select Shared Calendar</span>
                <button type="button" onClick={() => setCalPickerOpen(false)}
                  style={{ background: 'none', border: 'none', color: C.muted, fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}>×</button>
              </div>
              {calPickerLoading ? (
                <div style={{ padding: '12px 16px', fontSize: 12, color: C.muted }}>Loading calendars…</div>
              ) : !calPickerList?.length ? (
                <div style={{ padding: '12px 16px', fontSize: 12, color: C.muted }}>No calendars found. Connect Google Calendar first.</div>
              ) : (
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {calPickerList.map(cal => (
                    <button key={cal.id} type="button"
                      onClick={() => { setS('shared_cal_id', cal.id); setCalPickerOpen(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        background: shared.shared_cal_id === cal.id ? 'rgba(42,238,253,0.08)' : 'none',
                        border: 'none', borderBottom: `1px solid ${C.border}`,
                        padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                      }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: cal.backgroundColor || C.cyan, flexShrink: 0, display: 'inline-block' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: C.offWhite, fontWeight: shared.shared_cal_id === cal.id ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cal.summary}</div>
                        {cal.description && <div style={{ fontSize: 10, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cal.description}</div>}
                      </div>
                      {shared.shared_cal_id === cal.id && <span style={{ fontSize: 10, color: C.cyan, flexShrink: 0 }}>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <Divider />

          <SectionLabel>Weekend View</SectionLabel>
          <div style={row}>
            <span style={lbl}>Grid start</span>
            <select value={shared.grid_start_hour ?? 7} onChange={e => setS('grid_start_hour', Number(e.target.value))} style={{ ...smallInput, flex: '0 0 auto' }}>
              {Array.from({ length: 12 }, (_, i) => i + 5).map(h => (
                <option key={h} value={h}>{h < 12 ? `${h} AM` : '12 PM'}</option>
              ))}
            </select>
            <span style={lbl}>Grid end</span>
            <select value={shared.grid_end_hour ?? 21} onChange={e => setS('grid_end_hour', Number(e.target.value))} style={{ ...smallInput, flex: '0 0 auto' }}>
              {Array.from({ length: 10 }, (_, i) => i + 17).map(h => (
                <option key={h} value={h}>{h === 12 ? '12 PM' : h < 12 ? `${h} AM` : `${h-12} PM`}</option>
              ))}
            </select>
          </div>
          <div style={{ ...row, alignItems: 'flex-start' }}>
            <span style={{ ...lbl, paddingTop: 4 }}>Naptime</span>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <div onClick={() => setS('naptime_enabled', !shared.naptime_enabled)}
                  style={{ width: 40, height: 22, borderRadius: 11, background: shared.naptime_enabled ? C.cyan : C.border, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', top: 3, left: shared.naptime_enabled ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: C.white, transition: 'left 0.2s' }} />
                </div>
                <span style={{ fontSize: 12, color: shared.naptime_enabled ? C.cyan : C.muted }}>{shared.naptime_enabled ? 'On' : 'Off'}</span>
              </label>
              {shared.naptime_enabled && (
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Start</div>
                    <input type="time" value={shared.naptime_start || '13:30'} onChange={e => setS('naptime_start', e.target.value)}
                      style={{ ...inputBase, padding: '8px 10px', fontSize: 13, colorScheme: 'dark', width: 120 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Duration (min)</div>
                    <input type="number" min={15} max={240} value={shared.naptime_duration_mins ?? 120} onChange={e => setS('naptime_duration_mins', Number(e.target.value))}
                      style={{ ...inputBase, padding: '8px 10px', fontSize: 13, width: 90 }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <Divider />

          <SectionLabel>Coverage Options</SectionLabel>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.6 }}>
            Edit the dropdown options shown on the No School page.
          </div>
          {(shared.coverage_options || []).map((opt, idx) => (
            <div key={opt.value}
              draggable
              onDragStart={e => { e.dataTransfer.setData('text/plain', String(idx)); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
              onDragLeave={() => setDragOverIdx(null)}
              onDrop={e => { e.preventDefault(); const from = parseInt(e.dataTransfer.getData('text/plain')); if (from !== idx) moveCovOpt(from, idx); setDragOverIdx(null); }}
              onDragEnd={() => setDragOverIdx(null)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, background: dragOverIdx === idx ? C.border : C.navy, padding: '10px 12px', cursor: 'grab', borderLeft: dragOverIdx === idx ? `2px solid ${C.cyanBright}` : '2px solid transparent', transition: 'background 0.1s' }}>
              <span style={{ fontSize: 12, color: C.muted, cursor: 'grab', userSelect: 'none', marginRight: 2 }}>⠿</span>
              <EmojiPicker value={opt.emoji} onChange={v => setCovOpt(idx, 'emoji', v)} />
              <input value={opt.label} onChange={e => setCovOpt(idx, 'label', e.target.value)} style={{ ...smallInput, flex: 1 }} />
              <span style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', flexShrink: 0, opacity: 0.5 }}>{opt.value}</span>
              <button type="button" onClick={() => removeCovOpt(idx)}
                style={{ ...mkBtn('ghost', false), fontSize: 14, padding: '2px 6px', flexShrink: 0 }}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input placeholder="New option label…" value={newCovLabel} onChange={e => setNewCovLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCovOpt()} style={{ ...smallInput, flex: 1 }} />
            <button type="button" onClick={addCovOpt} disabled={!newCovLabel.trim()}
              style={{ ...mkBtn('primary', !newCovLabel.trim()), padding: '8px 16px', fontSize: 12 }}>Add</button>
          </div>

          <Divider />

          {/* ── Children ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <SectionLabel>Children</SectionLabel>
            <button type="button" onClick={addChild}
              style={{ ...mkBtn('primary', false), padding: '6px 14px', fontSize: 11 }}>+ Add Child</button>
          </div>
          {(shared.children || []).length === 0 && (
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>No children added yet.</div>
          )}
          {(shared.children || []).map((child, ci) => (
            <div key={child.id || ci} style={{ background: C.navy, border: `1px solid ${C.border}`, padding: '16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <input value={child.name} onChange={e => setChild(ci, 'name', e.target.value)}
                  placeholder="Child's name…"
                  style={{ ...inputBase, padding: '8px 12px', fontSize: 14, fontWeight: 700, flex: 1, marginRight: 8 }} />
                <button type="button" onClick={() => removeChild(ci)}
                  style={{ ...mkBtn('danger', false), padding: '6px 10px', fontSize: 11 }}>Remove</button>
              </div>
              <div style={{ ...row, flexWrap: 'wrap', rowGap: 8 }}>
                <span style={{ ...lbl, minWidth: 120 }}>School cal URL</span>
                <input value={child.school_cal_url || ''} onChange={e => setChild(ci, 'school_cal_url', e.target.value)}
                  placeholder="https://… (.ics, .pdf, or webpage)" style={{ ...smallInput, minWidth: 0, flex: '1 1 140px' }} />
                <button type="button" onClick={() => scrapeChild(ci)} disabled={scraping[ci] || !child.school_cal_url}
                  style={{ ...mkBtn('primary', scraping[ci] || !child.school_cal_url), padding: '8px 12px', fontSize: 11, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {calScrapeLabel(child.school_cal_url, scraping[ci])}
                </button>
              </div>
              {/* ── ICS result ── */}
              {child.school_cal_type === 'ics' && child.no_school_keywords?.length > 0 && (
                <div style={{ background: C.navyDark, border: `1px solid ${C.border}`, padding: '10px 12px', marginBottom: 10, marginTop: -6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showCalDates[ci] ? 8 : 0 }}>
                    <button type="button" onClick={() => setShowCalDates(s => ({ ...s, [ci]: !s[ci] }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}>
                      <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>
                        ✓ {child.school_cal_dates?.length ? `${child.school_cal_dates.length} no-school days from ICS` : `${child.no_school_keywords.length} keywords from ICS`}
                      </span>
                      <span style={{ fontSize: 10, color: C.muted }}>{showCalDates[ci] ? '▲ hide' : '▼ show'}</span>
                    </button>
                    <button type="button"
                      onClick={() => { setChild(ci, 'school_cal_type', null); setChild(ci, 'school_cal_dates', null); setChild(ci, 'no_school_keywords', []); setChild(ci, 'school_cal_url', ''); setShowCalDates(s => ({ ...s, [ci]: false })); }}
                      style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', padding: '3px 8px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Replace Calendar
                    </button>
                  </div>
                  {showCalDates[ci] && (child.school_cal_dates?.length ? (
                    <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {child.school_cal_dates.slice(0, 30).map((d, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                          <span style={{ color: C.offWhite }}>{d.label}</span>
                          <span style={{ color: C.muted }}>{d.date_start === d.date_end ? d.date_start : `${d.date_start} – ${d.date_end}`}</span>
                        </div>
                      ))}
                      {child.school_cal_dates.length > 30 && <span style={{ fontSize: 10, color: C.muted }}>…and {child.school_cal_dates.length - 30} more</span>}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(child.no_school_keywords || []).map((kw, ki) => (
                        <span key={ki} style={{ fontSize: 10, color: C.offWhite, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, padding: '2px 7px' }}>{kw}</span>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* ── PDF / webpage result ── */}
              {(child.school_cal_type === 'pdf' || child.school_cal_type === 'webpage') && child.school_cal_dates?.length > 0 && (
                <div style={{ background: C.navyDark, border: `1px solid ${C.border}`, padding: '10px 12px', marginBottom: 10, marginTop: -6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showCalDates[ci] ? 8 : 0 }}>
                    <button type="button" onClick={() => setShowCalDates(s => ({ ...s, [ci]: !s[ci] }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}>
                      <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>✓ {child.school_cal_dates.length} no-school days from {child.school_cal_type.toUpperCase()}</span>
                      <span style={{ fontSize: 10, color: C.muted }}>{showCalDates[ci] ? '▲ hide' : '▼ show'}</span>
                    </button>
                    <button type="button"
                      onClick={() => { setChild(ci, 'school_cal_type', null); setChild(ci, 'school_cal_dates', null); setChild(ci, 'no_school_keywords', []); setChild(ci, 'school_cal_url', ''); setShowCalDates(s => ({ ...s, [ci]: false })); }}
                      style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', padding: '3px 8px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Replace Calendar
                    </button>
                  </div>
                  {showCalDates[ci] && (
                    <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {[...(child.school_cal_dates || [])].sort((a, b) => a.date_start.localeCompare(b.date_start)).map((d, i) => {
                        const fmt = s => { const [y, m, day] = s.split('-'); return new Date(+y, +m - 1, +day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
                        const range = d.date_start === d.date_end ? fmt(d.date_start) : `${fmt(d.date_start)} – ${fmt(d.date_end)}`;
                        return (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 11, padding: '3px 0', borderBottom: i < child.school_cal_dates.length - 1 ? `1px solid rgba(255,255,255,0.04)` : 'none' }}>
                            <span style={{ color: C.offWhite, flex: 1, paddingRight: 12 }}>{d.label}</span>
                            <span style={{ color: C.muted, whiteSpace: 'nowrap', flexShrink: 0 }}>{range}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>No-School Keywords</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {(child.no_school_keywords || []).map((kw, ki) => (
                    <div key={ki} style={{ display: 'flex', alignItems: 'center', gap: 4, background: C.navyDark, border: `1px solid ${C.border}`, padding: '4px 8px' }}>
                      <span style={{ fontSize: 12, color: C.offWhite }}>{kw}</span>
                      <button type="button" onClick={() => setChild(ci, 'no_school_keywords', child.no_school_keywords.filter((_, j) => j !== ki))}
                        style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
                <input placeholder="Add keyword (Enter)…"
                  onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { setChild(ci, 'no_school_keywords', [...(child.no_school_keywords || []), e.target.value.trim()]); e.target.value = ''; }}}
                  style={{ ...smallInput, width: '100%' }} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Summer Camp Weeks</div>
                <textarea
                  value={child.summer_weeks || ''}
                  onChange={e => setChild(ci, 'summer_weeks', e.target.value)}
                  rows={4}
                  placeholder={"June 22-26 – Week 1\nJune 29 - July 3 – Week 2"}
                  style={{ ...inputBase, width: '100%', resize: 'vertical', fontSize: 12, padding: '8px 12px' }}
                />
              </div>
            </div>
          ))}

          <Divider />

          {/* ── Meal Planner Settings ── */}
          <SectionLabel>Meal Planner</SectionLabel>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', marginBottom: 12 }}>
              <div onClick={() => setS('show_weekend_meals', !shared.show_weekend_meals)}
                style={{ width: 40, height: 22, borderRadius: 11, background: shared.show_weekend_meals ? C.cyan : C.border, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: 3, left: shared.show_weekend_meals ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: C.white, transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: 13, color: shared.show_weekend_meals ? C.offWhite : C.muted }}>Show Saturday & Sunday meals</span>
            </label>
            {(shared.children || []).length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Show lunch column for</div>
                {(shared.children || []).map(child => child.name ? (
                  <label key={child.id || child.name} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 8 }}>
                    <input type="checkbox"
                      checked={(shared.kid_lunches || []).includes(child.name)}
                      onChange={e => {
                        const list = shared.kid_lunches || [];
                        setS('kid_lunches', e.target.checked ? [...list, child.name] : list.filter(n => n !== child.name));
                      }}
                      style={{ accentColor: C.cyanBright, width: 15, height: 15 }} />
                    <span style={{ fontSize: 13, color: C.offWhite }}>{child.name}</span>
                  </label>
                ) : null)}
              </div>
            )}
          </div>

          <Divider />

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={saveShared} disabled={saving}
              style={{ ...mkBtn('primary', saving), padding: '16px 32px', fontSize: 13 }}>
              {saving ? 'Saving…' : 'Save Family Settings'}
            </button>
            {saveStatus === 'saved' && <span style={{ fontSize: 12, color: C.green }}>✓ Saved</span>}
            {saveStatus === 'error' && <span style={{ fontSize: 12, color: C.red }}>✗ Failed to save</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Signup Flow ───────────────────────────────────────────────────────────────

function SignupFlow({ onDone, onLogin }) {
  const [step, setStep] = useState('signup'); // signup | check-email | google | done
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSignup(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !pass.trim()) { setErr('All fields required.'); return; }
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${BACKEND}/api/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password: pass, familyName: familyName.trim() || name.trim() + ' Family' }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setStep('check-email');
      } else {
        setErr(data.error || 'Signup failed. Try again.');
      }
    } catch (_) { setErr('Could not reach server.'); }
    setLoading(false);
  }

  const cardStyle = { background: C.navy, border: `1px solid ${C.border}`, padding: '32px', maxWidth: 420, width: '100%', margin: '0 auto' };

  if (step === 'check-email') return (
    <div style={{ minHeight: '100vh', background: C.navyDark, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 20px' }}>
      <div style={cardStyle}>
        <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>Almost there</div>
        <h2 style={{ color: C.white, fontFamily: "'Georgia', serif", fontWeight: 400, margin: '0 0 16px', fontSize: 22 }}>Check your email</h2>
        <div style={{ width: 32, height: 2, background: C.cyanBright, marginBottom: 24 }} />
        <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>
          We sent a verification link to <strong style={{ color: C.offWhite }}>{email}</strong>. Click the link to verify your account, then come back here to sign in.
        </p>
        <button onClick={onLogin}
          style={{ ...mkBtn('primary', false), padding: '14px 24px', fontSize: 13, width: '100%' }}>
          Back to Sign In
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.navyDark, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 20px' }}>
      <form onSubmit={handleSignup} style={cardStyle}>
        <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>Create account</div>
        <h2 style={{ color: C.white, fontFamily: "'Georgia', serif", fontWeight: 400, margin: '0 0 16px', fontSize: 22 }}>Family Dashboard</h2>
        <div style={{ width: 32, height: 2, background: C.cyanBright, marginBottom: 28 }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} autoFocus
            style={{ ...inputBase, fontFamily: "'Inter', sans-serif", width: '100%' }} />
          <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)}
            style={{ ...inputBase, fontFamily: "'Inter', sans-serif", width: '100%' }} autoComplete="email" />
          <input type="password" placeholder="Password" value={pass} onChange={e => setPass(e.target.value)}
            style={{ ...inputBase, fontFamily: "'Inter', sans-serif", width: '100%' }} autoComplete="new-password" />
          <input placeholder="Family name (e.g. The Smiths)" value={familyName} onChange={e => setFamilyName(e.target.value)}
            style={{ ...inputBase, fontFamily: "'Inter', sans-serif", width: '100%' }} />
        </div>

        {err && <div style={{ color: C.red, fontSize: 13, marginTop: 12 }}>{err}</div>}

        <button type="submit" disabled={loading}
          style={{ ...mkBtn('primary', loading), padding: '16px 24px', fontSize: 13, width: '100%', marginTop: 20 }}>
          {loading ? 'Creating account…' : 'Create Account'}
        </button>
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button type="button" onClick={onLogin}
            style={{ background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            Already have an account? Sign in →
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Reset Password Flow ───────────────────────────────────────────────────────

function ResetPasswordFlow({ token, onDone }) {
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [status, setStatus] = useState(null); // null | 'saving' | 'done' | 'error'
  const [errMsg, setErrMsg] = useState('');

  async function handleReset(e) {
    e.preventDefault();
    if (pass.length < 8) { setErrMsg('Password must be at least 8 characters.'); return; }
    if (pass !== pass2) { setErrMsg('Passwords do not match.'); return; }
    setStatus('saving'); setErrMsg('');
    try {
      const res = await fetch(`${BACKEND}/api/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ token, password: pass }),
      });
      const data = await res.json();
      if (res.ok && data.ok) { setStatus('done'); }
      else { setErrMsg(data.error || 'Reset failed.'); setStatus(null); }
    } catch (_) { setErrMsg('Could not reach server.'); setStatus(null); }
  }

  const cardStyle = { background: C.navy, border: `1px solid ${C.border}`, padding: '32px', maxWidth: 420, width: '100%', margin: '0 auto' };
  return (
    <div style={{ minHeight: '100vh', background: C.navyDark, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 20px' }}>
      {status === 'done' ? (
        <div style={cardStyle}>
          <h2 style={{ color: C.white, fontFamily: "'Georgia', serif", fontWeight: 400, margin: '0 0 16px', fontSize: 22 }}>Password updated!</h2>
          <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>Your password has been changed. Sign in with your new password.</p>
          <button onClick={onDone} style={{ ...mkBtn('primary', false), padding: '14px 24px', fontSize: 13, width: '100%' }}>Back to Sign In</button>
        </div>
      ) : (
        <form onSubmit={handleReset} style={cardStyle}>
          <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>Password Reset</div>
          <h2 style={{ color: C.white, fontFamily: "'Georgia', serif", fontWeight: 400, margin: '0 0 16px', fontSize: 22 }}>Choose a new password</h2>
          <div style={{ width: 32, height: 2, background: C.cyanBright, marginBottom: 28 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input type="password" placeholder="New password (8+ chars)" value={pass} onChange={e => setPass(e.target.value)} autoFocus
              style={{ ...inputBase, fontFamily: "'Inter', sans-serif", width: '100%' }} autoComplete="new-password" />
            <input type="password" placeholder="Confirm new password" value={pass2} onChange={e => setPass2(e.target.value)}
              style={{ ...inputBase, fontFamily: "'Inter', sans-serif", width: '100%' }} autoComplete="new-password" />
          </div>
          {errMsg && <div style={{ color: C.red, fontSize: 13, marginTop: 12 }}>{errMsg}</div>}
          <button type="submit" disabled={status === 'saving'}
            style={{ ...mkBtn('primary', status === 'saving'), padding: '16px 24px', fontSize: 13, width: '100%', marginTop: 20 }}>
            {status === 'saving' ? 'Saving…' : 'Set New Password'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Forgot Password Flow ──────────────────────────────────────────────────────

function ForgotPasswordFlow({ onBack }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    try {
      await fetch(`${BACKEND}/api/request-password-reset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ email: email.trim() }),
      });
      setStatus('sent');
    } catch (_) { setStatus('error'); }
  }

  const cardStyle = { background: C.navy, border: `1px solid ${C.border}`, padding: '32px', maxWidth: 420, width: '100%', margin: '0 auto' };
  return (
    <div style={{ minHeight: '100vh', background: C.navyDark, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 20px' }}>
      {status === 'sent' ? (
        <div style={cardStyle}>
          <h2 style={{ color: C.white, fontFamily: "'Georgia', serif", fontWeight: 400, margin: '0 0 16px', fontSize: 22 }}>Check your email</h2>
          <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>If an account exists for <strong style={{ color: C.offWhite }}>{email}</strong>, you'll receive a reset link shortly.</p>
          <button onClick={onBack} style={{ ...mkBtn('primary', false), padding: '14px 24px', fontSize: 13, width: '100%' }}>Back to Sign In</button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ ...cardStyle, background: C.navy }}>
          <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>Account Recovery</div>
          <h2 style={{ color: C.white, fontFamily: "'Georgia', serif", fontWeight: 400, margin: '0 0 16px', fontSize: 22 }}>Forgot password?</h2>
          <div style={{ width: 32, height: 2, background: C.cyanBright, marginBottom: 28 }} />
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>Enter your email and we'll send you a reset link.</p>
          <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} autoFocus
            style={{ ...inputBase, fontFamily: "'Inter', sans-serif", width: '100%', marginBottom: 12 }} />
          {status === 'error' && <div style={{ color: C.red, fontSize: 13, marginBottom: 8 }}>Could not send email. Try again.</div>}
          <button type="submit" disabled={status === 'sending' || !email.trim()}
            style={{ ...mkBtn('primary', status === 'sending' || !email.trim()), padding: '16px 24px', fontSize: 13, width: '100%', marginBottom: 16 }}>
            {status === 'sending' ? 'Sending…' : 'Send Reset Link'}
          </button>
          <div style={{ textAlign: 'center' }}>
            <button type="button" onClick={onBack}
              style={{ background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              ← Back to sign in
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Meal Planner ──────────────────────────────────────────────────────────────

function MealPlanner({ settings }) {
  const [weekStart, setWeekStart]       = useState(() => mondayOf(new Date()));
  const [meals, setMeals]               = useState({});
  const [links, setLinks]               = useState({});  // mealKey → url string
  const [editingLink, setEditingLink]   = useState(null); // mealKey currently editing
  const [responsible, setResponsible]   = useState({});  // mealKey → string
  const [loading, setLoading]           = useState(true);
  const [suggestions, setSuggestions]   = useState({});  // slotKey → string[]
  const [loadingSugg, setLoadingSugg]   = useState({});  // slotKey → bool
  const [savingKey, setSavingKey]       = useState(null);

  const showWeekend = settings?.show_weekend_meals;
  const kidsWithLunch = settings?.kid_lunches || [];
  const memberNames = (settings?.members || []).map(m => m.display_name || m.name).filter(Boolean);

  const DAYS = ['monday','tuesday','wednesday','thursday','friday',
    ...(showWeekend ? ['saturday','sunday'] : [])];
  const DAY_LABEL = { monday:'Monday', tuesday:'Tuesday', wednesday:'Wednesday',
    thursday:'Thursday', friday:'Friday', saturday:'Saturday', sunday:'Sunday' };

  // Slots: dinner first, then per-kid lunch
  const SLOTS = [
    { type: 'dinner', child: '', label: 'Dinner' },
    ...kidsWithLunch.map(k => ({ type: 'lunch', child: k, label: `${k}'s Lunch` })),
  ];

  function slotKey(type, child) { return `${type}__${child}`; }
  function mealKey(day, type, child) { return `${day}|${type}|${child}`; }

  useEffect(() => { loadMeals(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMeals() {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/meals?week_start=${weekStart}`, { credentials: 'include' });
      if (reloadIf401(r)) return;
      const data = await r.json();
      const map = {};
      const rmap = {};
      const lmap = {};
      for (const m of (data.meals || [])) {
        const k = mealKey(m.day, m.meal_type, m.child_name);
        map[k] = m.meal_text;
        if (m.responsible) rmap[k] = m.responsible;
        if (m.link) lmap[k] = m.link;
      }
      setMeals(map);
      setResponsible(rmap);
      setLinks(lmap);
    } catch (_) {}
    setLoading(false);
  }

  async function saveMeal(day, type, child, text, resp, lnk) {
    const k = mealKey(day, type, child);
    setSavingKey(k);
    const respValue = resp !== undefined ? resp : (responsible[k] || 'both');
    const linkValue = lnk !== undefined ? lnk : (links[k] || '');
    try {
      await fetch(`${BACKEND}/api/meals`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ week_start: weekStart, day, meal_type: type, child_name: child, meal_text: text !== undefined ? text : (meals[k] || ''), responsible: respValue, link: linkValue }),
      });
    } catch (_) {}
    setSavingKey(null);
  }

  async function getSuggestions(type, child) {
    const sk = slotKey(type, child);
    if (loadingSugg[sk]) return;
    setLoadingSugg(p => ({ ...p, [sk]: true }));
    try {
      const qs = `meal_type=${type}${child ? `&child_name=${encodeURIComponent(child)}` : ''}`;
      const r = await fetch(`${BACKEND}/api/meals/suggestions?${qs}`, { credentials: 'include' });
      const data = await r.json();
      setSuggestions(p => ({ ...p, [sk]: data.suggestions || [] }));
    } catch (_) {}
    setLoadingSugg(p => ({ ...p, [sk]: false }));
  }

  function weekLabel() {
    const s = new Date(weekStart + 'T12:00:00');
    const e = new Date(weekStart + 'T12:00:00');
    e.setDate(e.getDate() + (showWeekend ? 6 : 4));
    const fmt = { month: 'short', day: 'numeric' };
    return s.toLocaleDateString('en-US', fmt) + ' – ' + e.toLocaleDateString('en-US', { ...fmt, year: 'numeric' });
  }

  function shiftWeek(n) {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + n * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  }

  function getDayDate(day) {
    const idx = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].indexOf(day);
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + idx);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  if (loading) return (
    <div style={{ padding: '48px 16px', textAlign: 'center', color: C.muted, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
      Loading meals…
    </div>
  );

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 16px 80px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>Meal Planner</div>
          <div style={{ fontSize: 'clamp(16px,4vw,20px)', fontWeight: 400, color: C.white, fontFamily: "'Georgia', serif" }}>{weekLabel()}</div>
          <div style={{ width: 32, height: 2, background: C.cyanBright, marginTop: 10 }} />
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', paddingTop: 4 }}>
          <button onClick={() => shiftWeek(-1)} style={{ ...mkBtn('ghost', false), padding: '7px 12px', fontSize: 12 }}>← Prev</button>
          <button onClick={() => setWeekStart(mondayOf(new Date()))} style={{ ...mkBtn('ghost', false), padding: '7px 10px', fontSize: 11 }}>Now</button>
          <button onClick={() => shiftWeek(1)}  style={{ ...mkBtn('ghost', false), padding: '7px 12px', fontSize: 12 }}>Next →</button>
        </div>
      </div>

      {/* Meal Ideas — one panel per slot type, above all days */}
      <div style={{ background: C.navy, border: `1px solid ${C.border}`, padding: '16px 20px', marginBottom: 18 }}>
        <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 14 }}>
          Meal Ideas
        </div>
        {SLOTS.map((slot, si) => {
          const sk = slotKey(slot.type, slot.child);
          const suggs = suggestions[sk] || [];
          const loadingS = !!loadingSugg[sk];
          return (
            <div key={sk} style={{ marginBottom: si < SLOTS.length - 1 ? 16 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: suggs.length ? 10 : 0 }}>
                <span style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: 56 }}>{slot.label}</span>
                <button onClick={() => getSuggestions(slot.type, slot.child)}
                  style={{ ...mkBtn('ghost', loadingS), padding: '4px 0', fontSize: 11 }}>
                  {loadingS ? 'Getting ideas…' : suggs.length ? '↻ Refresh' : '💡 Get ideas'}
                </button>
              </div>
              {suggs.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {suggs.map(s => (
                    <div key={s} style={{ display: 'flex', alignItems: 'stretch', background: C.navyDark, border: `1px solid ${C.border}`, borderRadius: 3, overflow: 'hidden' }}>
                      <span style={{ color: C.offWhite, padding: '5px 10px', fontSize: 12 }}>{s}</span>
                      <a href={`https://www.allrecipes.com/search?q=${encodeURIComponent(s)}`}
                        target="_blank" rel="noopener noreferrer"
                        title="AllRecipes"
                        style={{ display: 'flex', alignItems: 'center', padding: '5px 7px', borderLeft: `1px solid ${C.border}`, color: C.cyan, fontSize: 11, textDecoration: 'none', lineHeight: 1 }}>
                        AR
                      </a>
                      <a href={`https://www.food.com/search/${encodeURIComponent(s)}`}
                        target="_blank" rel="noopener noreferrer"
                        title="Food.com"
                        style={{ display: 'flex', alignItems: 'center', padding: '5px 7px', borderLeft: `1px solid ${C.border}`, color: C.cyan, fontSize: 11, textDecoration: 'none', lineHeight: 1 }}>
                        FC
                      </a>
                      <a href={`https://www.epicurious.com/search?q=${encodeURIComponent(s)}`}
                        target="_blank" rel="noopener noreferrer"
                        title="Epicurious"
                        style={{ display: 'flex', alignItems: 'center', padding: '5px 7px', borderLeft: `1px solid ${C.border}`, color: C.cyan, fontSize: 11, textDecoration: 'none', lineHeight: 1 }}>
                        EP
                      </a>
                    </div>
                  ))}
                  <button onClick={() => setSuggestions(p => ({ ...p, [sk]: [] }))}
                    style={{ background: 'none', border: 'none', color: C.muted, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: '5px 4px' }}>
                    ✕
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Day rows */}
      {DAYS.map(day => (
        <div key={day} style={{ background: C.navy, border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.cyan, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>
            {DAY_LABEL[day]} <span style={{ color: C.muted, fontWeight: 400 }}>· {getDayDate(day)}</span>
          </div>

          {SLOTS.map((slot, si) => {
            const mk = mealKey(day, slot.type, slot.child);
            const val = meals[mk] || '';
            const isSaving = savingKey === mk;
            const respVal = responsible[mk] || 'both';
            return (
              <div key={slot.label} style={{ marginBottom: si < SLOTS.length - 1 ? 18 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: 72 }}>{slot.label}</span>
                  {memberNames.length > 0 && (
                    <select
                      value={respVal}
                      onChange={e => {
                        const v = e.target.value;
                        setResponsible(p => ({ ...p, [mk]: v }));
                        saveMeal(day, slot.type, slot.child, undefined, v);
                      }}
                      style={{ background: C.navyDark, border: `1px solid ${C.border}`, color: C.muted, fontSize: 11, padding: '2px 6px', fontFamily: 'inherit', cursor: 'pointer' }}>
                      <option value="both">Both</option>
                      {memberNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  )}
                  {isSaving && <span style={{ fontSize: 10, color: C.muted }}>saving…</span>}
                </div>
                <input
                  value={val}
                  onChange={e => setMeals(p => ({ ...p, [mk]: e.target.value }))}
                  onBlur={e => saveMeal(day, slot.type, slot.child, e.target.value)}
                  placeholder={`What's for ${slot.label.toLowerCase()}?`}
                  style={{ ...inputBase, width: '100%', fontSize: 14 }}
                />
                {/* Link: show inline if set, or "add link" button */}
                {links[mk] && editingLink !== mk ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <a href={links[mk].match(/^https?:\/\//) ? links[mk] : `https://${links[mk]}`} target="_blank" rel="noopener noreferrer"
                      style={{ color: C.cyan, fontSize: 11, textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                      {links[mk].replace(/^https?:\/\/(www\.)?/, '').slice(0, 50)}
                    </a>
                    <button onClick={() => setEditingLink(mk)}
                      style={{ background: 'none', border: 'none', color: C.muted, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>edit</button>
                    <button onClick={() => { setLinks(p => { const n = { ...p }; delete n[mk]; return n; }); setEditingLink(null); saveMeal(day, slot.type, slot.child, undefined, undefined, ''); }}
                      style={{ background: 'none', border: 'none', color: C.muted, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>x</button>
                  </div>
                ) : editingLink === mk ? (
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <input
                      value={links[mk] || ''}
                      onChange={e => setLinks(p => ({ ...p, [mk]: e.target.value }))}
                      onBlur={e => { const v = e.target.value.trim(); if (v) { setLinks(p => ({ ...p, [mk]: v })); saveMeal(day, slot.type, slot.child, undefined, undefined, v); } else { setLinks(p => { const n = { ...p }; delete n[mk]; return n; }); saveMeal(day, slot.type, slot.child, undefined, undefined, ''); } setEditingLink(null); }}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                      placeholder="https://..."
                      autoFocus
                      style={{ ...inputBase, flex: 1, fontSize: 11, padding: '4px 8px' }}
                    />
                  </div>
                ) : val.trim() ? (
                  <button onClick={() => { setEditingLink(mk); if (!links[mk]) setLinks(p => ({ ...p, [mk]: '' })); }}
                    style={{ background: 'none', border: 'none', color: C.muted, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0 0 0', letterSpacing: '0.04em' }}>
                    + add link
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}

      {SLOTS.length === 1 && kidsWithLunch.length === 0 && (
        <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', marginTop: 12, lineHeight: 1.7 }}>
          Enable kid lunch columns in <button onClick={() => {}} style={{ background: 'none', border: 'none', color: C.cyan, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, padding: 0 }}>Settings → Family Settings</button>.
        </div>
      )}
    </div>
  );
}

// ── Weekday at a Glance ───────────────────────────────────────────────────────

function WeekdayPlanner({ settings }) {
  const [weekStart, setWeekStart]         = useState(() => mondayOf(new Date()));
  const [events, setEvents]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [responsibilities, setResponsibilities] = useState({}); // eventId → value
  const [savingId, setSavingId]           = useState(null);

  const members = settings?.members || [];
  const memberNames = members.map(m => m.display_name || m.name).filter(Boolean);
  const userIcons = settings?.user_icons || {};

  function memberIcon(name) {
    if (!name) return '';
    const lower = name.toLowerCase();
    if (lower === memberNames[0]?.toLowerCase()) return userIcons.user1 || userIcons.nick || '🔵';
    if (lower === memberNames[1]?.toLowerCase()) return userIcons.user2 || userIcons.geri || '🔴';
    return '🎒';
  }

  const DAYS = ['monday','tuesday','wednesday','thursday','friday'];
  const DAY_LABEL = { monday:'Monday', tuesday:'Tuesday', wednesday:'Wednesday', thursday:'Thursday', friday:'Friday' };

  useEffect(() => { loadEvents(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadEvents(forceRefresh = false) {
    const cacheKey = `weekday_cache_${weekStart}`;
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { data, savedAt } = JSON.parse(cached);
          if (Date.now() - savedAt < 21600000) { // 6 hour TTL — matches server cache
            const evts = data.events || [];
            setEvents(evts);
            const map = {};
            for (const e of evts) { if (e.responsible) map[e.id] = e.responsible; }
            setResponsibilities(map);
            setLoading(false);
            return;
          }
        }
      } catch (_) {}
    }
    setLoading(true);
    setEvents([]);
    try {
      const r = await fetch(`${BACKEND}/api/weekday?week_start=${weekStart}${forceRefresh ? '&refresh=1' : ''}`, { credentials: 'include' });
      if (reloadIf401(r)) return;
      const data = await r.json();
      try { localStorage.setItem(cacheKey, JSON.stringify({ data, savedAt: Date.now() })); } catch (_) {}
      const evts = data.events || [];
      setEvents(evts);
      // Populate local responsibility state from loaded data
      const map = {};
      for (const e of evts) { if (e.responsible) map[e.id] = e.responsible; }
      setResponsibilities(map);
    } catch (_) {}
    setLoading(false);
  }

  async function saveResponsibility(eventId, weekS, value) {
    setSavingId(eventId);
    setResponsibilities(p => ({ ...p, [eventId]: value }));
    try {
      await fetch(`${BACKEND}/api/weekday/responsibility`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ week_start: weekS, event_id: eventId, responsible: value }),
      });
    } catch (_) {}
    setSavingId(null);
  }

  function shiftWeek(n) {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + n * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  }

  function getDayDate(day) {
    const idx = DAYS.indexOf(day);
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + idx);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function fmtTime(dt) {
    if (!dt) return null;
    try { return new Date(dt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
    catch (_) { return null; }
  }

  function weekLabel() {
    const s = new Date(weekStart + 'T12:00:00');
    const e = new Date(weekStart + 'T12:00:00');
    e.setDate(e.getDate() + 4);
    const fmt = { month: 'short', day: 'numeric' };
    return s.toLocaleDateString('en-US', fmt) + ' – ' + e.toLocaleDateString('en-US', { ...fmt, year: 'numeric' });
  }

  function isThisWeek() { return weekStart === mondayOf(new Date()); }

  const eventsByDay = {};
  for (const day of DAYS) eventsByDay[day] = events.filter(e => e.day === day);

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 16px 80px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>Weekday at a Glance</div>
          <div style={{ fontSize: 'clamp(16px,4vw,20px)', fontWeight: 400, color: C.white, fontFamily: "'Georgia', serif" }}>{weekLabel()}</div>
          <div style={{ width: 32, height: 2, background: C.cyanBright, marginTop: 10 }} />
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', paddingTop: 4 }}>
          <button onClick={() => shiftWeek(-1)} style={{ ...mkBtn('ghost', false), padding: '7px 12px', fontSize: 12 }}>← Prev</button>
          <button onClick={() => setWeekStart(mondayOf(new Date()))} disabled={isThisWeek()} style={{ ...mkBtn('ghost', isThisWeek()), padding: '7px 10px', fontSize: 11 }}>Now</button>
          <button onClick={() => shiftWeek(1)}  style={{ ...mkBtn('ghost', false), padding: '7px 12px', fontSize: 12 }}>Next →</button>
          <button onClick={() => loadEvents(true)} style={{ ...mkBtn('ghost', loading), padding: '7px 10px', fontSize: 11 }}>↺</button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '48px 0', textAlign: 'center', color: C.muted, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Classifying events with AI…
        </div>
      ) : events.length === 0 ? (
        <div style={{ background: C.navy, border: `1px solid ${C.border}`, padding: '32px 24px', textAlign: 'center', color: C.muted, fontSize: 13 }}>
          No activities found for this week.
          <div style={{ marginTop: 8, fontSize: 12 }}>Make sure your shared and personal calendars are configured in Settings.</div>
        </div>
      ) : (
        DAYS.map(day => {
          const dayEvts = eventsByDay[day];
          if (dayEvts.length === 0) return null;
          return (
            <div key={day} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: C.cyan, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8, display: 'flex', gap: 8, alignItems: 'baseline' }}>
                {DAY_LABEL[day]}
                <span style={{ color: C.muted, fontWeight: 400, fontSize: 10 }}>{getDayDate(day)}</span>
              </div>
              <div style={{ background: C.navy, border: `1px solid ${C.border}` }}>
                {dayEvts.map((evt, i) => {
                  const startTime = evt.allDay ? 'All day' : fmtTime(evt.start.dateTime);
                  const endTime   = evt.allDay ? null    : fmtTime(evt.end?.dateTime);
                  const timeStr   = startTime && endTime ? `${startTime} – ${endTime}` : (startTime || '');
                  const icon = memberIcon(evt.person);
                  const isLast = i === dayEvts.length - 1;
                  const respValue = responsibilities[evt.id] || '';

                  return (
                    <div key={evt.id} style={{ padding: '14px 18px', borderBottom: isLast ? 'none' : `1px solid ${C.border}`, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      {/* Time column */}
                      <div style={{ minWidth: 90, flexShrink: 0 }}>
                        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{timeStr}</div>
                      </div>

                      {/* Event details */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
                          <span style={{ fontSize: 14, color: C.white, fontWeight: 500 }}>{evt.summary}</span>
                          {evt.person && evt.person !== 'family' && (
                            <span style={{ fontSize: 11, color: C.muted }}>{evt.person}</span>
                          )}
                        </div>
                        {evt.location && (
                          <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>📍 {evt.location}</div>
                        )}
                        {/* Responsible parent dropdown for kid activities */}
                        {evt.isKidActivity && memberNames.length > 0 && (
                          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Taking:</span>
                            <select
                              value={respValue}
                              onChange={e => saveResponsibility(evt.id, weekStart, e.target.value)}
                              disabled={savingId === evt.id}
                              style={{ background: C.navyDark, border: `1px solid ${C.border}`, color: respValue ? C.white : C.muted, fontSize: 12, padding: '4px 8px', fontFamily: 'inherit', cursor: 'pointer' }}>
                              <option value="">Unassigned</option>
                              {memberNames.map(n => <option key={n} value={n}>{memberIcon(n)} {n}</option>)}
                              {memberNames.length >= 2 && <option value="both">Both</option>}
                            </select>
                            {savingId === evt.id && <span style={{ fontSize: 10, color: C.muted }}>saving…</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Feedback Widget ────────────────────────────────────────────────────────────

function FeedbackWidget({ page }) {
  const [open, setOpen]       = useState(false);
  const [type, setType]       = useState('bug');
  const [message, setMessage] = useState('');
  const [status, setStatus]   = useState(null); // null | 'sending' | 'sent' | 'error'

  async function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setStatus('sending');
    try {
      const r = await fetch(`${BACKEND}/api/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ type, message: message.trim(), page }),
      });
      if (r.ok) {
        setStatus('sent');
        setMessage('');
        setTimeout(() => { setOpen(false); setStatus(null); }, 2200);
      } else {
        setStatus('error');
      }
    } catch (_) { setStatus('error'); }
  }

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => { setOpen(o => !o); setStatus(null); }}
        title="Send feedback"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9000,
          width: 42, height: 42, borderRadius: '50%',
          background: C.navy, border: `1px solid ${C.border}`,
          color: C.muted, fontSize: 17, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)', transition: 'border-color 0.2s, color 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = C.cyan; e.currentTarget.style.color = C.cyan; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
      >
        ✦
      </button>

      {/* Panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 76, right: 24, zIndex: 9001,
          width: 300, background: C.navy, border: `1px solid ${C.border}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', padding: '20px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.cyan, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Feedback</div>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
          </div>

          {status === 'sent' ? (
            <div style={{ color: C.green, fontSize: 13, textAlign: 'center', padding: '12px 0' }}>
              Thanks! We got it. ✓
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {['bug', 'feature', 'other'].map(t => (
                  <button key={t} type="button" onClick={() => setType(t)}
                    style={{
                      flex: 1, padding: '6px 4px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                      background: type === t ? C.cyan : 'transparent',
                      border: `1px solid ${type === t ? C.cyan : C.border}`,
                      color: type === t ? C.navyDark : C.muted, fontWeight: type === t ? 700 : 400,
                    }}>
                    {t === 'bug' ? '🐛 Bug' : t === 'feature' ? '✨ Feature' : 'Other'}
                  </button>
                ))}
              </div>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={type === 'bug' ? 'What went wrong?' : type === 'feature' ? 'What would you like?' : 'Tell us anything…'}
                rows={4}
                style={{ ...inputBase, resize: 'vertical', minHeight: 80, fontSize: 13 }}
              />
              {status === 'error' && <div style={{ color: C.red, fontSize: 12 }}>Something went wrong. Try again.</div>}
              <button type="submit" disabled={status === 'sending' || !message.trim()}
                style={{ ...mkBtn('primary', status === 'sending' || !message.trim()), padding: '10px', fontSize: 12 }}>
                {status === 'sending' ? 'Sending…' : 'Send Feedback'}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}

// ── Landing Page ──────────────────────────────────────────────────────────────

function LandingPage({ onSignIn, onSignUp }) {
  const LP = {
    navy: "#00294C",
    navyDark: "#001d38",
    cyan: "#1a8a98",
    cyanBright: "#2AEEFD",
    text: "#0f172a",
    textMid: "#334155",
    textLight: "#4b5563",
    border: "#e2e8f0",
    bgLight: "#f8fafc",
  };

  const features = [
    { icon: "📅", title: "Google Calendar Sync", desc: "Connects directly to your existing Google Calendars. No double-entry, no extra apps — your schedule is already there." },
    { icon: "✈️", title: "Travel Conflict Detection", desc: "Automatically spots when your travel overlaps with school pickups, kids' activities, or your partner's trips — before it becomes a problem." },
    { icon: "🤖", title: "AI-Powered Scheduling", desc: "Ask questions, create events, and get schedule summaries in plain English. Claude handles the calendar logistics so you don't have to." },
    { icon: "🏫", title: "No-School Day Planner", desc: "Track every school closure, early dismissal, and break. Plan camp, coverage, and backup care — all in one place." },
    { icon: "✓", title: "Shared Family To-Do", desc: "One task list for the whole household. Assign to-dos, set target dates, and see what's done — no more sticky notes or missed tasks." },
    { icon: "🗓", title: "Weekend at a Glance", desc: "A visual grid of your weekend: who's doing what, when, and where. Perfect for the Saturday-morning sanity check." },
  ];

  const steps = [
    { num: "1", title: "Connect Your Google Calendar", desc: "Sign up and link your Google Calendar in one click. We read your calendar in real time — nothing is stored or duplicated." },
    { num: "2", title: "Invite Your Partner", desc: "Add your co-parent or partner so both schedules are visible in one shared view. Each person connects their own account." },
    { num: "3", title: "Stop Missing Things", desc: "Get a weekly email digest, spot conflicts instantly, and use AI to manage your calendar in plain English." },
  ];

  return (
    <div style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", background: "#fff", color: LP.text, overflowX: "hidden" }}>

      {/* ── NAV ── */}
      <nav style={{ background: LP.navyDark, borderBottom: "1px solid rgba(255,255,255,0.07)", padding: "0 24px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 30, height: 30, background: LP.cyanBright, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>🏠</div>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: "-0.02em" }}>HerroLab</span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={onSignIn}
            style={{ background: "none", border: "1px solid rgba(255,255,255,0.18)", color: "rgba(255,255,255,0.85)", padding: "8px 18px", borderRadius: 6, fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>
            Sign In
          </button>
          <button onClick={onSignUp}
            style={{ background: LP.cyanBright, border: "none", color: LP.navyDark, padding: "8px 18px", borderRadius: 6, fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>
            Get Started
          </button>
        </div>
      </nav>

      <main>
      {/* ── HERO ── */}
      <section style={{ background: `linear-gradient(155deg, ${LP.navyDark} 0%, ${LP.navy} 55%, #004a7c 100%)`, padding: "88px 24px 104px", textAlign: "center" }}>
        <div style={{ maxWidth: 740, margin: "0 auto" }}>
          <div style={{ display: "inline-block", background: "rgba(43,238,253,0.12)", border: "1px solid rgba(43,238,253,0.28)", color: LP.cyanBright, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "6px 16px", borderRadius: 999, marginBottom: 32 }}>
            For families who use Google Calendar
          </div>
          <h1 style={{ color: "#fff", fontSize: "clamp(34px, 7vw, 60px)", fontWeight: 800, lineHeight: 1.08, margin: "0 0 26px", letterSpacing: "-0.03em" }}>
            The family dashboard<br />busy parents deserve
          </h1>
          <p style={{ color: "rgba(255,255,255,0.68)", fontSize: "clamp(16px, 2.5vw, 19px)", lineHeight: 1.75, margin: "0 0 48px", maxWidth: 580, marginLeft: "auto", marginRight: "auto" }}>
            Connect your Google Calendars, invite your partner, and get AI-powered tools to coordinate kids' activities, catch conflicts early, and stay on top of family logistics.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={onSignUp}
              style={{ background: LP.cyanBright, border: "none", color: LP.navyDark, padding: "16px 34px", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "-0.01em" }}>
              Get Started Free →
            </button>
            <button onClick={onSignIn}
              style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.22)", color: "#fff", padding: "16px 34px", borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <div style={{ background: LP.navy, padding: "18px 24px", textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, margin: 0, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          Works with your existing Google Calendar &nbsp;·&nbsp; No new apps for the kids &nbsp;·&nbsp; Set up in under 5 minutes
        </p>
      </div>

      {/* ── FEATURES ── */}
      <section style={{ padding: "84px 24px", background: LP.bgLight }}>
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LP.cyan, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 14 }}>Everything you need</div>
            <h2 style={{ fontSize: "clamp(26px, 5vw, 40px)", fontWeight: 800, margin: "0 0 18px", letterSpacing: "-0.025em", color: LP.text }}>
              Built for how families actually schedule
            </h2>
            <p style={{ color: LP.textLight, fontSize: "clamp(15px, 2vw, 18px)", maxWidth: 520, margin: "0 auto", lineHeight: 1.7 }}>
              Stop juggling five different tools. One dashboard that connects to your calendar and actually helps.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 20 }}>
            {features.map(f => (
              <div key={f.title} style={{ background: "#fff", border: `1px solid ${LP.border}`, borderRadius: 12, padding: "30px 26px" }}>
                <div style={{ fontSize: 34, marginBottom: 18, lineHeight: 1 }}>{f.icon}</div>
                <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: LP.text }}>{f.title}</h3>
                <p style={{ color: LP.textLight, fontSize: 14, lineHeight: 1.75, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding: "84px 24px", background: "#fff" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LP.cyan, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 14 }}>How it works</div>
            <h2 style={{ fontSize: "clamp(26px, 5vw, 40px)", fontWeight: 800, margin: 0, letterSpacing: "-0.025em", color: LP.text }}>
              Up and running in minutes
            </h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {steps.map((step, i) => (
              <div key={step.num} style={{ display: "flex", gap: 28, alignItems: "flex-start", paddingBottom: i < steps.length - 1 ? 52 : 0, position: "relative" }}>
                {i < steps.length - 1 && (
                  <div style={{ position: "absolute", left: 19, top: 44, bottom: 0, width: 2, background: LP.border }} />
                )}
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: LP.navyDark, color: LP.cyanBright, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15, flexShrink: 0, border: `2px solid ${LP.cyanBright}` }}>
                  {step.num}
                </div>
                <div style={{ paddingTop: 6 }}>
                  <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 8px", color: LP.text }}>{step.title}</h3>
                  <p style={{ color: LP.textLight, fontSize: 15, lineHeight: 1.75, margin: 0 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── QUOTE ── */}
      <section style={{ padding: "72px 24px", background: "#f0f9ff", borderTop: "1px solid #dbeafe", borderBottom: "1px solid #dbeafe" }}>
        <div style={{ maxWidth: 660, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 20 }}>💬</div>
          <blockquote style={{ fontSize: "clamp(17px, 2.5vw, 22px)", fontStyle: "italic", color: LP.textMid, lineHeight: 1.65, margin: "0 0 24px", fontWeight: 500 }}>
            "Managing two working parents' travel schedules around school pickups and weekend sports was chaos. Having one place where everything is synced and conflicts are flagged automatically is a game-changer."
          </blockquote>
          <div style={{ fontSize: 13, color: LP.textLight, fontWeight: 600 }}>— Working parent, 3 kids, using Google Calendar since 2016</div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ background: `linear-gradient(155deg, ${LP.navyDark} 0%, ${LP.navy} 100%)`, padding: "84px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ color: "#fff", fontSize: "clamp(26px, 5vw, 40px)", fontWeight: 800, margin: "0 0 18px", letterSpacing: "-0.025em" }}>
            Ready to get your family organized?
          </h2>
          <p style={{ color: "rgba(255,255,255,0.62)", fontSize: "clamp(15px, 2vw, 18px)", lineHeight: 1.75, margin: "0 0 44px" }}>
            Works with the Google Calendar you already use.<br />Setup takes less than 5 minutes.
          </p>
          <button onClick={onSignUp}
            style={{ background: LP.cyanBright, border: "none", color: LP.navyDark, padding: "18px 42px", borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "-0.01em" }}>
            Create Your Family Dashboard →
          </button>
          <div style={{ marginTop: 22 }}>
            <button onClick={onSignIn}
              style={{ background: "none", border: "none", color: "rgba(255,255,255,0.62)", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
              Already have an account? Sign in →
            </button>
          </div>
        </div>
      </section>
      </main>

      {/* ── FOOTER ── */}
      <footer style={{ background: LP.navyDark, borderTop: "1px solid rgba(255,255,255,0.06)", padding: "28px 24px", textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.58)", fontSize: 12, margin: 0, letterSpacing: "0.04em" }}>
          © {new Date().getFullYear()} HerroLab · Family Dashboard · herrolab.com
        </p>
      </footer>

      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────────

export default function App() {
  const [authed, setAuthed] = useState(null);
  const [meData, setMeData] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass]   = useState("");
  const [loginErr, setLoginErr]     = useState("");
  const [showLogin, setShowLogin]   = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [screenMsg, setScreenMsg]   = useState(null); // { title, body, action }
  const [g1Pose, setG1Pose]         = useState("pose-wave");
  const [g2Pose, setG2Pose]         = useState("pose-idle");
  const [bubble, setBubble]         = useState({ text: "", visible: false });
  const [page, setPage]             = useState("home"); // home | calendar | noschool | todos | weekend | settings
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus]   = useState(null); // null | "sent" | "error"
  const [appSettings, setAppSettings]   = useState(null);
  const [showLoveNoteCompose, setShowLoveNoteCompose] = useState(false);
  const [loveNoteText, setLoveNoteText]               = useState("");
  const [loveNoteSending, setLoveNoteSending]         = useState(false);
  const [loveNoteSent, setLoveNoteSent]               = useState(false);
  const [incomingNote, setIncomingNote]               = useState(null); // { id, message, from_name }

  // Handle URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('verify')) {
      const token = params.get('verify');
      window.history.replaceState({}, '', '/');
      fetch(`${BACKEND}/api/verify-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ token }),
      })
        .then(r => r.json())
        .then(d => {
          if (d.ok) setScreenMsg({ title: 'Email verified!', body: 'Your account is verified. Sign in to get started.', action: 'Sign In' });
          else setScreenMsg({ title: 'Verification failed', body: d.error || 'The link may have expired.', action: 'Back' });
        })
        .catch(() => setScreenMsg({ title: 'Error', body: 'Could not verify. Try again.', action: 'Back' }));
    }

    if (params.get('invite')) {
      const token = params.get('invite');
      window.history.replaceState({}, '', '/');
      // Check auth first, then accept invite or store token for after login
      fetch(`${BACKEND}/api/me`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          if (d.authenticated) {
            return fetch(`${BACKEND}/api/accept-invite`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              credentials: 'include', body: JSON.stringify({ token }),
            }).then(r => r.json()).then(data => {
              if (data.ok) {
                setScreenMsg({ title: 'Paired!', body: "You've joined the family dashboard. Welcome!", action: 'Continue' });
                setAuthed(true);
                loadMe();
              } else setScreenMsg({ title: 'Invite error', body: data.error || 'Could not accept invite.', action: 'Back' });
            });
          } else {
            // Store and show message
            sessionStorage.setItem('pendingInviteToken', token);
            setScreenMsg({ title: 'You were invited!', body: 'Sign in or create an account, then the invite will be accepted automatically.', action: 'Continue' });
          }
        })
        .catch(() => {});
    }

    if (params.get('oauth') === 'success') {
      window.history.replaceState({}, '', '/');
      loadMe();
    }

    if (params.get('reset')) {
      setResetToken(params.get('reset'));
      window.history.replaceState({}, '', '/');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMe() {
    try {
      const r = await fetch(`${BACKEND}/api/me`, { credentials: 'include' });
      const d = await r.json();
      setAuthed(d.authenticated);
      if (d.authenticated) setMeData(d);
    } catch (_) { setAuthed(false); }
  }

  useEffect(() => { loadMe(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authed) {
      fetch(`${BACKEND}/api/app-settings`, { credentials: "include" })
        .then(r => r.json())
        .then(d => setAppSettings(d))
        .catch(() => {});

      // Check for incoming love note
      fetch(`${BACKEND}/api/love-note/pending`, { credentials: "include" })
        .then(r => r.json())
        .then(d => { if (d.note) setIncomingNote(d.note); })
        .catch(() => {});

      // Accept pending invite if any
      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        sessionStorage.removeItem('pendingInviteToken');
        fetch(`${BACKEND}/api/accept-invite`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ token: pendingToken }),
        }).then(r => r.json()).then(data => {
          if (data.ok) { loadMe(); }
        }).catch(() => {});
      }
    }
  }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Greet when login screen appears
  useEffect(() => {
    if (authed === false) {
      setG1Pose("pose-wave");
      setG2Pose("pose-idle");
      setBubble({ text: "Hi there! 👋", visible: true });
      const t = setTimeout(() => setBubble(b => ({ ...b, visible: false })), 2600);
      return () => clearTimeout(t);
    }
  }, [authed]);

  function launchConfetti() {
    const colors = ['#38bdf8', '#a78bfa', '#f472b6', '#34d399', '#fbbf24', '#ffffff'];
    for (let i = 0; i < 70; i++) {
      const el = document.createElement('div');
      el.className = 'confetti-piece';
      el.style.top = '-10px';
      el.style.left = Math.random() * 100 + 'vw';
      el.style.background = colors[Math.floor(Math.random() * colors.length)];
      el.style.animationName = 'confetti-fall';
      el.style.animationDuration = (1.2 + Math.random() * 1.5) + 's';
      el.style.animationDelay = (Math.random() * 0.5) + 's';
      el.style.animationFillMode = 'forwards';
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 3000);
    }
  }

  async function sendLoveNote() {
    if (!loveNoteText.trim() || loveNoteSending) return;
    setLoveNoteSending(true);
    try {
      await fetch(`${BACKEND}/api/love-note`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ message: loveNoteText.trim() }),
      });
      setLoveNoteSent(true);
      setLoveNoteText("");
      setTimeout(() => { setShowLoveNoteCompose(false); setLoveNoteSent(false); }, 2000);
    } catch (_) {}
    setLoveNoteSending(false);
  }

  async function dismissNote() {
    if (!incomingNote) return;
    try {
      await fetch(`${BACKEND}/api/love-note/${incomingNote.id}/dismiss`, {
        method: "POST", credentials: "include",
      });
    } catch (_) {}
    setIncomingNote(null);
  }

  async function handleLogin(e) {
    e.preventDefault(); setLoginErr("");
    try {
      const res = await fetch(`${BACKEND}/api/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ email: loginEmail, password: loginPass }),
      });
      if (res.ok) {
        setG1Pose("pose-celebrate");
        setG2Pose("pose-celebrate");
        setBubble({ text: "Yay! Let's go! 🎉", visible: true });
        launchConfetti();
        setTimeout(() => loadMe(), 1800);
      } else {
        setLoginErr("Invalid email or password.");
        setG1Pose("pose-shake");
        setG2Pose("pose-shake");
        setBubble({ text: "Oops! Wrong password 😬", visible: true });
        setTimeout(() => { setG1Pose("pose-idle"); setG2Pose("pose-idle"); setBubble(b => ({ ...b, visible: false })); }, 1800);
      }
    } catch (_) { setLoginErr("Could not reach server."); }
  }

  async function handleLogout() {
    await fetch(`${BACKEND}/api/logout`, { method: "POST", credentials: "include" });
    setAuthed(false); setMeData(null); setLoginEmail(""); setLoginPass(""); setPage("home"); setShowLogin(false);
  }

  async function handleSendWeekAhead() {
    setSendingEmail(true); setEmailStatus(null);
    try {
      const res = await fetch(`${BACKEND}/api/send-week-ahead`, { method: "POST", credentials: "include" });
      setEmailStatus(res.ok ? "sent" : "error");
    } catch (_) { setEmailStatus("error"); }
    setSendingEmail(false);
  }

  const familyName = appSettings?.family_name || meData?.workspace?.family_name || 'Family';

  // Loading
  if (authed === null && !screenMsg) return (
    <div style={{ minHeight: "100vh", background: C.navy, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: C.muted, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase" }}>Loading</div>
    </div>
  );

  // Screen message (verify, invite, oauth result)
  if (screenMsg) return (
    <div style={{ minHeight: "100vh", background: C.navyDark, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 20px" }}>
      <div style={{ background: C.navy, border: `1px solid ${C.border}`, padding: "32px", maxWidth: 420, width: "100%" }}>
        <div style={{ fontSize: 10, color: C.cyan, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>Notice</div>
        <h2 style={{ color: C.white, fontFamily: "'Georgia', serif", fontWeight: 400, margin: "0 0 16px", fontSize: 22 }}>{screenMsg.title}</h2>
        <div style={{ width: 32, height: 2, background: C.cyanBright, marginBottom: 24 }} />
        <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: "0 0 24px" }}>{screenMsg.body}</p>
        <button onClick={() => { setScreenMsg(null); }}
          style={{ ...mkBtn("primary", false), padding: "14px 24px", fontSize: 13, width: "100%" }}>
          {screenMsg.action}
        </button>
      </div>
    </div>
  );

  // Password reset flow
  if (resetToken) return (
    <ResetPasswordFlow token={resetToken} onDone={() => setResetToken(null)} />
  );

  // Forgot password flow
  if (!authed && showForgot) return (
    <ForgotPasswordFlow onBack={() => setShowForgot(false)} />
  );

  // Signup flow
  if (!authed && showSignup) return (
    <SignupFlow onDone={() => { setShowSignup(false); }} onLogin={() => setShowSignup(false)} />
  );

  // Landing page (shown to unauthenticated visitors before they click Sign In)
  if (!authed && !showLogin) return (
    <LandingPage onSignIn={() => setShowLogin(true)} onSignUp={() => { setShowLogin(false); setShowSignup(true); }} />
  );

  // Login
  if (!authed) return (
    <div style={{ minHeight: "100vh", background: "white", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 20px" }}>
      <div style={{ width: "100%", maxWidth: 420, position: "relative" }}>

        {/* Speech bubble above girl1 */}
        <div className={`speech-bubble${bubble.visible ? ' visible' : ''}`}>{bubble.text}</div>

        {/* Mascots — rendered first so card's solid bg covers their lower halves */}
        <div className="mascots">
          <img className={`mascot ${g1Pose}`} src="/girl1.png" alt="" loading="lazy" width="200" height="300" />
          <img className={`mascot ${g2Pose}`} src="/girl2.png" alt="" loading="lazy" width="200" height="300" />
        </div>

        {/* Login card — z-index 2, solid background covers girls from waist down */}
        <form onSubmit={handleLogin} className="login-card"
          style={{ background: C.navy, borderRadius: 16, padding: "28px 32px 36px", boxShadow: "0 12px 48px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.07)", display: "flex", flexDirection: "column", gap: 0, fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 10, color: C.cyan, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>Welcome back</div>
            <h1 style={{ color: C.white, fontSize: "clamp(20px, 5vw, 26px)", fontWeight: 400, margin: 0, lineHeight: 1.2, fontFamily: "'Georgia', serif" }}>Family Dashboard</h1>
            <div style={{ width: 36, height: 2, background: C.cyanBright, marginTop: 12 }} />
          </div>
          <input type="email" placeholder="Email address" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
            style={{ ...inputBase, fontFamily: "'Inter', sans-serif", marginBottom: 2, width: "100%" }}
            autoFocus autoComplete="email"
            onFocus={() => { setG1Pose("pose-wave"); setG2Pose("pose-peek"); setBubble({ text: "Welcome back! 😊", visible: true }); }}
            onBlur={() => { setBubble(b => ({ ...b, visible: false })); setG1Pose("pose-idle"); setG2Pose("pose-idle"); }}
          />
          <input type="password" placeholder="Password" value={loginPass} onChange={e => setLoginPass(e.target.value)}
            style={{ ...inputBase, fontFamily: "'Inter', sans-serif", marginBottom: 16, width: "100%" }}
            autoComplete="current-password"
            onFocus={() => { setG1Pose("pose-cover"); setG2Pose("pose-cover"); setBubble({ text: "We won't peek! 🙈", visible: true }); }}
            onBlur={() => { setBubble(b => ({ ...b, visible: false })); setG1Pose("pose-idle"); setG2Pose("pose-idle"); }}
          />
          {loginErr && <div style={{ color: C.red, fontSize: 14, marginBottom: 12 }}>{loginErr}</div>}
          <button type="submit" style={{ ...mkBtn("primary", false), padding: "16px 24px", fontSize: 13, width: "100%" }}>
            Sign In
          </button>
          <div style={{ marginTop: 16, textAlign: "center", display: "flex", flexDirection: "column", gap: 8 }}>
            <button type="button" onClick={() => setShowForgot(true)}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
              Forgot password?
            </button>
            <button type="button" onClick={() => setShowSignup(true)}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
              New here? Create an account →
            </button>
            <button type="button" onClick={() => setShowLogin(false)}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit", marginTop: 4 }}>
              ← Back to home
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const NAV_SECTIONS = [
    { id: "travel",    label: "Travel" },
    { id: "events",    label: "Events" },
    { id: "noschool",  label: "No School" },
    { id: "todos",     label: "To-Do" },
    { id: "weekend",   label: "Weekend" },
    { id: "weekday",   label: "Weekday" },
    { id: "meals",     label: "Meals" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.navyDark, fontFamily: "'Inter', 'Helvetica Neue', sans-serif", color: C.white }}>

      {/* Top bar */}
      <div style={{ background: C.navy, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 10 }}>
        {/* Row 1: logo + actions */}
        <div style={{ padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 48 }}>
          <button onClick={() => setPage("home")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
            <span style={{ fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 400, color: C.white, whiteSpace: "nowrap" }}>{familyName}</span>
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
            <button
              onClick={() => { setShowLoveNoteCompose(true); setLoveNoteSent(false); }}
              title="Send a love note 💌"
              style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", padding: "6px 8px", color: "#f9a8d4", fontSize: 15, WebkitTapHighlightColor: "transparent" }}>
              💌
            </button>
            <span style={{ width: 1, height: 16, background: C.border, display: 'inline-block', margin: '0 2px' }} />
            <button onClick={() => setPage("settings")}
              title="Settings"
              style={{ ...mkBtn("ghost", false), fontSize: 18, padding: "8px 10px", lineHeight: 1, color: page === "settings" ? C.cyanBright : C.muted }}>
              ⚙
            </button>
            <span style={{ width: 1, height: 16, background: C.border, display: 'inline-block', margin: '0 2px' }} />
            <button onClick={handleLogout} style={{ ...mkBtn("ghost", false), fontSize: 11, padding: "8px 4px 8px 8px" }}>
              Sign Out
            </button>
          </div>
        </div>
        {/* Row 2: scrollable nav */}
        <div className="nav-scroll" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", borderTop: `1px solid ${C.border}`, scrollbarWidth: "none" }}>
          <div style={{ display: "flex", alignItems: "center", padding: "0 16px", height: 36, gap: 4, minWidth: "max-content" }}>
            {NAV_SECTIONS.map(s => (
              <button key={s.id} onClick={() => setPage(s.id)}
                style={{ background: page === s.id ? "rgba(56,189,248,0.1)" : "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: page === s.id ? C.cyanBright : C.muted, fontWeight: page === s.id ? 700 : 400, padding: "6px 10px", whiteSpace: "nowrap", flexShrink: 0, borderBottom: page === s.id ? `2px solid ${C.cyanBright}` : "2px solid transparent", WebkitTapHighlightColor: "transparent" }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Home screen */}
      {page === "home" && (
        <div style={{ maxWidth: 620, margin: "0 auto", padding: "48px 16px 0" }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 10, color: C.cyan, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>{familyName}</div>
            <h1 style={{ color: C.white, fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 400, margin: 0, fontFamily: "'Georgia', serif" }}>Family Dashboard</h1>
            <div style={{ width: 32, height: 2, background: C.cyanBright, marginTop: 14 }} />
          </div>
          <DayGlance settings={appSettings} />
          <WeekDigest settings={appSettings} />
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 16, marginBottom: 16, marginTop: -8 }}>
            <button
              onClick={handleSendWeekAhead}
              disabled={sendingEmail}
              style={{ ...mkBtn("ghost", sendingEmail), fontSize: 12, padding: 0, letterSpacing: "0.04em" }}
            >
              {sendingEmail ? "sending…" : emailStatus === "sent" ? "sent ✓" : emailStatus === "error" ? "error ✗" : "send email"}
            </button>
            <a
              href={`${BACKEND}/api/week-ahead?today=${new Date().toLocaleDateString('en-CA')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: C.muted, textDecoration: "none", letterSpacing: "0.04em" }}
            >
              week ahead →
            </a>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            {[
              { id: "travel",    icon: "✈️", title: "Travel",    desc: "Upcoming trips & conflict detection — 90-day view" },
              { id: "events",    icon: "📅", title: "Events",    desc: "Add, update & browse calendar events" },
              { id: "noschool",  icon: "🏫", title: "No School", desc: "No-school days, camp & coverage" },
              { id: "todos",     icon: "✓",  title: "To-Do",     desc: "Shared task list with target dates" },
              { id: "weekend",   icon: "🗓", title: "Weekend",   desc: "This weekend's schedule at a glance" },
              { id: "weekday",   icon: "📋", title: "Weekday",   desc: "Mon–Fri activities, kid pickups & assignments" },
              { id: "meals",     icon: "🍽", title: "Meals",     desc: "Weekly dinner & lunch planner" },
              { id: "settings",  icon: "⚙️", title: "Settings",  desc: "Names, icons, email, calendar & more" },
            ].map(card => (
              <button key={card.id} onClick={() => setPage(card.id)}
                style={{ background: C.navy, border: `1px solid ${C.border}`, padding: "28px 20px", color: C.white, cursor: "pointer", textAlign: "left", fontFamily: "inherit", display: "block", width: "100%", transition: "border-color 0.2s" }}>
                <div style={{ fontSize: 28, marginBottom: 14 }}>{card.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.cyan, marginBottom: 8 }}>{card.title}</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{card.desc}</div>
              </button>
            ))}
          </div>
          {/* Girls in bottom corners */}
          <img src="/girl1.png" alt="" loading="lazy" width="170" height="170" style={{ position: "fixed", bottom: 0, left: 0, width: 170, transform: "translateY(50%)", pointerEvents: "none", zIndex: 0, userSelect: "none" }} />
          <img src="/girl2.png" alt="" loading="lazy" width="170" height="170" style={{ position: "fixed", bottom: 0, right: 0, width: 170, transform: "translateY(50%)", pointerEvents: "none", zIndex: 0, userSelect: "none" }} />
        </div>
      )}

      {/* Travel */}
      {page === "travel" && <TravelPage settings={appSettings} />}

      {/* Events */}
      {page === "events" && <EventsPage settings={appSettings} />}

      {/* No School Manager */}
      {page === "noschool" && <NoSchoolManager settings={appSettings} />}

      {/* Todo Manager */}
      {page === "todos" && <TodoManager settings={appSettings} />}

      {/* Weekend Planner */}
      {page === "weekend" && <WeekendPlanner settings={appSettings} />}

      {/* Weekday */}
      {page === "weekday" && <WeekdayPlanner settings={appSettings} />}

      {/* Meals */}
      {page === "meals" && <MealPlanner settings={appSettings} />}

      {/* Settings */}
      {page === "settings" && <SettingsManager settings={appSettings} meData={meData} onSaved={s => { setAppSettings(s); loadMe(); }} />}

      {/* Feedback widget — visible on all authenticated pages */}
      <FeedbackWidget page={page} />

      {/* ── Love Note Compose Modal ── */}
      {showLoveNoteCompose && (
        <div onClick={() => { setShowLoveNoteCompose(false); setLoveNoteSent(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "#1a0a14", border: "1.5px solid #f472b6", borderRadius: 2, padding: "36px 32px", maxWidth: 420, width: "100%", position: "relative", boxShadow: "0 0 60px rgba(244,114,182,0.18)" }}>
            {/* Close */}
            <button onClick={() => { setShowLoveNoteCompose(false); setLoveNoteSent(false); }}
              style={{ position: "absolute", top: 12, right: 14, background: "none", border: "none", color: "#f9a8d4", fontSize: 18, cursor: "pointer", lineHeight: 1, opacity: 0.7 }}>✕</button>

            {loveNoteSent ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 14 }}>💌</div>
                <div style={{ color: "#f9a8d4", fontFamily: "'Georgia', serif", fontSize: 18, marginBottom: 8 }}>Sent with love</div>
                <div style={{ color: "rgba(249,168,212,0.55)", fontSize: 13 }}>They'll see it on their next visit.</div>
              </div>
            ) : (
              <>
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>💌</div>
                  <div style={{ color: "#f9a8d4", fontFamily: "'Georgia', serif", fontSize: 17, marginBottom: 4 }}>Send a love note</div>
                  <div style={{ color: "rgba(249,168,212,0.45)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>just for them · shown on next visit</div>
                </div>
                <textarea
                  value={loveNoteText}
                  onChange={e => setLoveNoteText(e.target.value.slice(0, 500))}
                  placeholder="Write something sweet…"
                  rows={5}
                  autoFocus
                  style={{ ...inputBase, width: "100%", resize: "none", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(244,114,182,0.3)", color: "#fce7f3", fontSize: 15, lineHeight: 1.7, marginBottom: 8 }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "rgba(249,168,212,0.35)" }}>{loveNoteText.length}/500</span>
                  <button
                    onClick={sendLoveNote}
                    disabled={!loveNoteText.trim() || loveNoteSending}
                    style={{ background: "transparent", border: "1.5px solid #f472b6", color: "#f9a8d4", borderRadius: 0, padding: "10px 24px", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", cursor: loveNoteText.trim() ? "pointer" : "not-allowed", opacity: loveNoteText.trim() ? 1 : 0.4, fontFamily: "inherit", transition: "all 0.2s" }}>
                    {loveNoteSending ? "Sending…" : "Send ♥"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Incoming Love Note Modal ── */}
      {incomingNote && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "#1a0a14", border: "1.5px solid #f472b6", borderRadius: 2, padding: "44px 36px", maxWidth: 440, width: "100%", position: "relative", boxShadow: "0 0 80px rgba(244,114,182,0.22)", textAlign: "center" }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>💌</div>
            <div style={{ fontSize: 10, color: "rgba(249,168,212,0.5)", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 6 }}>
              a note from {incomingNote.from_name}
            </div>
            <div style={{ width: 28, height: 1, background: "rgba(244,114,182,0.3)", margin: "0 auto 24px" }} />
            <p style={{ color: "#fce7f3", fontSize: 17, lineHeight: 1.75, fontFamily: "'Georgia', serif", margin: "0 0 32px", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
              {incomingNote.message}
            </p>
            <button
              onClick={dismissNote}
              style={{ background: "transparent", border: "1.5px solid rgba(244,114,182,0.5)", color: "#f9a8d4", borderRadius: 0, padding: "10px 32px", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
              ♥ Close
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input::placeholder, textarea::placeholder { color: ${C.muted}; }
        input:focus, textarea:focus { border-color: ${C.cyan} !important; }
        select { color: ${C.white}; }
        select option { background: ${C.navyDark}; }
        body { background: ${C.navyDark}; margin: 0; -webkit-text-size-adjust: 100%; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: ${C.navy}; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; }
        .nav-scroll::-webkit-scrollbar { display: none; }
        @media (hover: hover) { button:hover:not(:disabled) { opacity: 0.8; } }
      `}</style>
    </div>
  );
}
